import Phaser from 'phaser';
import { SCENE_KEYS } from '@/config/constants';
import { gameSettings } from '@/config/gameSettings';
import { OTAMA_ASSETS, OtamaAssetKey } from '@/config/otamaAssets';
import { getPlatformAdapter } from '@/platform';
import { useGameStore } from '@/store/gameStore';
import { SoundSystem } from '@/systems/audio/SoundSystem';
import { ParticleFxSystem } from '@/systems/particles/ParticleFxSystem';

type PetChoice = 'dog' | 'cat';
type GameScreen =
  | 'intro'
  | 'select'
  | 'product'
  | 'instructions'
  | 'countdown'
  | 'playing'
  | 'loss'
  | 'score'
  | 'register'
  | 'ranking';

type FleaTarget = {
  sprite: Phaser.GameObjects.Image;
  hitZone: Phaser.GameObjects.Rectangle;
  speed: number;
  drift: number;
};

type TickBoss = {
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  hitZone: Phaser.GameObjects.Rectangle;
  legs: Phaser.GameObjects.Rectangle[];
  eyes: Phaser.GameObjects.Arc[];
  pupils: Phaser.GameObjects.Arc[];
  health: number;
  vx: number;
  vy: number;
  baseY: number;
  nextDodgeAtMs: number;
  trailAtMs: number;
};

type MiniTickTarget = {
  sprite: Phaser.GameObjects.Image;
  hitZone: Phaser.GameObjects.Rectangle;
  vx: number;
  vy: number;
  spawnedAtMs: number;
};

type BonusKind = 'time' | 'life';
type NavButtonTone = 'neutral' | 'primary' | 'warning';

type BonusCollectible = {
  container: Phaser.GameObjects.Container;
  hitZone: Phaser.GameObjects.Rectangle;
  kind: BonusKind;
  value: number;
  floatSpeed: number;
  drift: number;
  phase: number;
  activeAtMs: number;
};

type OttoIntroRig = {
  root: Phaser.GameObjects.Container;
  hitZone: Phaser.GameObjects.Rectangle;
  body: Phaser.GameObjects.Image;
  headGroup: Phaser.GameObjects.Container;
  leftEar: Phaser.GameObjects.Image;
  rightEar: Phaser.GameObjects.Image;
  leftEye: Phaser.GameObjects.Image;
  rightEye: Phaser.GameObjects.Image;
  leftEyeClosed: Phaser.GameObjects.Image;
  rightEyeClosed: Phaser.GameObjects.Image;
  leftBrow: Phaser.GameObjects.Image;
  rightBrow: Phaser.GameObjects.Image;
  noseMouth: Phaser.GameObjects.Image;
  closedMouth: Phaser.GameObjects.Image;
  leftPaw: Phaser.GameObjects.Image;
  rightPaw: Phaser.GameObjects.Image;
  scarf: Phaser.GameObjects.Image;
  bubble: Phaser.GameObjects.Container;
  speechText: Phaser.GameObjects.Text;
  baseLeftEye: Phaser.Math.Vector2;
  baseRightEye: Phaser.Math.Vector2;
  baseNoseMouth: Phaser.Math.Vector2;
};

type ReynaRig = {
  root: Phaser.GameObjects.Container;
  headGroup: Phaser.GameObjects.Container;
  tail: Phaser.GameObjects.Image;
  leftEar: Phaser.GameObjects.Image;
  rightEar: Phaser.GameObjects.Image;
  leftEye: Phaser.GameObjects.Image;
  rightEye: Phaser.GameObjects.Image;
  leftEyeClosed: Phaser.GameObjects.Image;
  rightEyeClosed: Phaser.GameObjects.Image;
  leftPupil: Phaser.GameObjects.Image;
  rightPupil: Phaser.GameObjects.Image;
  leftPupilGlint: Phaser.GameObjects.Ellipse;
  rightPupilGlint: Phaser.GameObjects.Ellipse;
  noseMouth: Phaser.GameObjects.Image;
  leftBrow: Phaser.GameObjects.Image;
  rightBrow: Phaser.GameObjects.Image;
  bubble: Phaser.GameObjects.Container;
  speechText: Phaser.GameObjects.Text;
  speechMessages: string[];
  speechIndex: number;
  speechBubbleBaseScale: number;
  hitZone: Phaser.GameObjects.Rectangle;
  baseLeftPupil: Phaser.Math.Vector2;
  baseRightPupil: Phaser.Math.Vector2;
  baseLeftPupilGlint: Phaser.Math.Vector2;
  baseRightPupilGlint: Phaser.Math.Vector2;
};

type RankingEntry = {
  name: string;
  contact: string;
  score: number;
  pet: PetChoice;
  createdAt: string;
};

type LeadEntry = {
  name: string;
  contact: string;
  createdAt: string;
};

const WIDTH = gameSettings.designWidth;
const HEIGHT = gameSettings.designHeight;
const RANKING_STORAGE_KEY = 'otama-flea-ranking';
const LEAD_STORAGE_KEY = 'otama-leads';
const DOG_PRODUCT_NAME = 'Nexus Max 3M';
const GAMEPLAY_INPUT_TOP_SAFE_Y = 150;
const OTTO_INTRO_MESSAGES = [
  '¡Hola! Soy OTTO.',
  'Toca las pulgas\nlo más rápido que puedas.',
  `${DOG_PRODUCT_NAME} protege\npor 3 meses.`,
  '¿Listo para jugar?',
];
const OTTO_REGISTER_MESSAGES = [
  'Registrate y entra\nal reto OTAMA.',
  'Tu nombre puede estar\nen la clasificacion.',
  'Juega, suma puntos\ny supera al lider.',
  'Yo te acompano.\nVamos a cazar pulgas!',
];
const REYNA_INTRO_MESSAGES = [
  'Miau! Soy REYNA.',
  'Tambien tengo\nmi propio reto.',
  'Elige Misiprox\ny juega conmigo.',
  'Yo soy rapida.\nPuedes seguirme?',
];
const REYNA_PRODUCT_MESSAGES = [
  'Misiprox me protege\ncontra pulgas.',
  'Toca rapido y suma\npuntos conmigo.',
  'Estoy lista para\nel reto touch.',
  'Miau! Vamos a ganar.',
];
const shouldExposeRuntimeState = import.meta.env.DEV;
const runtimeWindow = globalThis as typeof globalThis & {
  __OTAMA_GAME_SCREEN__?: GameScreen;
  __OTAMA_INTRO_OTTO_ACTIVE__?: boolean;
  __OTAMA_INTRO_SPEECH__?: string;
  __OTAMA_GAME_SCORE__?: number;
  __OTAMA_GAME_HAIR_TOUCHES__?: number;
  __OTAMA_GAME_LIVES__?: number;
  __OTAMA_GAME_FRENZY_ACTIVE__?: boolean;
  __OTAMA_GAME_FLEAS__?: Array<{ x: number; y: number; width: number; height: number }>;
  __OTAMA_GAME_TICK_BOSS__?: { x: number; y: number; health: number } | null;
  __OTAMA_GAME_DEBUG_HIT_TICK_BOSS__?: () => void;
  __OTAMA_GAME_MINI_TICKS__?: Array<{ x: number; y: number }>;
  __OTAMA_GAME_BONUSES__?: Array<{ x: number; y: number; kind: BonusKind; value: number }>;
  __OTAMA_GAME_RUN_DURATION_MS__?: number;
};

export class GameplayScene extends Phaser.Scene {
  private screen: GameScreen = 'intro';
  private selectedPet: PetChoice = 'dog';
  private score = 0;
  private hairTouches = 0;
  private elapsedMs = 0;
  private runDurationMs = gameSettings.gameplay.durationSeconds * 1000;
  private lastRunEndedByLoss = false;
  private acceptGameplayInputAtMs = 0;
  private lastFleaHitPointerId = -1;
  private lastFleaHitAtMs = -1;
  private readonly fleas: FleaTarget[] = [];
  private readonly miniTicks: MiniTickTarget[] = [];
  private readonly bonusCollectibles: BonusCollectible[] = [];
  private readonly fleaKillTimestamps: number[] = [];
  private tickBoss?: TickBoss;
  private tickBossSpawned = false;
  private tickBossWarningStarted = false;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private hairCueTimer?: Phaser.Time.TimerEvent;
  private scoreText?: Phaser.GameObjects.Text;
  private timerText?: Phaser.GameObjects.Text;
  private errorsText?: Phaser.GameObjects.Text;
  private livesText?: Phaser.GameObjects.Text;
  private readonly lifeIcons: Phaser.GameObjects.Arc[] = [];
  private frenzyUntilMs = 0;
  private frenzyLevel = 0;
  private frenzyOverlay?: Phaser.GameObjects.Rectangle;
  private frenzyText?: Phaser.GameObjects.Text;
  private ottoIntroRig?: OttoIntroRig;
  private ottoBlinkTimer?: Phaser.Time.TimerEvent;
  private ottoSpeechTimer?: Phaser.Time.TimerEvent;
  private ottoReactionTimer?: Phaser.Time.TimerEvent;
  private ottoScarfFlutterTimer?: Phaser.Time.TimerEvent;
  private ottoMouthTimer?: Phaser.Time.TimerEvent;
  private ottoRandomBarkTimer?: Phaser.Time.TimerEvent;
  private ottoSpeechIndex = 0;
  private ottoSpeechMessages = OTTO_INTRO_MESSAGES;
  private ottoIsReacting = false;
  private soundSystem!: SoundSystem;
  private particleFx!: ParticleFxSystem;
  private nameInput?: Phaser.GameObjects.DOMElement;
  private contactInput?: Phaser.GameObjects.DOMElement;
  private currentParticipantName = 'Participante';
  private currentParticipantContact = '';
  private currentRunSavedToRanking = false;

  constructor() {
    super(SCENE_KEYS.gameplay);
  }

  create() {
    this.soundSystem = new SoundSystem();
    this.particleFx = new ParticleFxSystem(this);
    this.input.setTopOnly(true);
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.handlePointerDown);

    const platform = getPlatformAdapter();
    useGameStore.getState().setPlatformLabel(platform.label);
    useGameStore.getState().setSceneReady(true);

    this.showIntro();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.handleShutdown, this);
  }

  update(_time: number, delta: number) {
    if (this.screen !== 'playing') {
      if (this.screen === 'intro' || this.screen === 'register') {
        this.updateIntroOttoPointerFollow();
      }

      return;
    }

    this.elapsedMs += delta;
    this.moveFleas(delta);
    this.moveMiniTicks(delta);
    this.moveBonusCollectibles(delta);
    this.moveTickBoss(delta);
    this.maybeSpawnTickBoss();
    this.updateFrenzyState();
    this.updateGameplayHud();
    this.syncGameplayAudio();

    if (this.elapsedMs >= this.runDurationMs) {
      this.endRun();
    }
  }

  private showIntro() {
    this.setScreen('intro');
    this.clearScreen();
    this.ottoSpeechMessages = OTTO_INTRO_MESSAGES;
    useGameStore.getState().setStatus('Pantalla inicial');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startMenuMusic();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.addIntroMenuAudioHitArea();
    this.addImage(OTAMA_ASSETS.introLogo, 230, 110, 300, 94);
    this.addImage(OTAMA_ASSETS.introTitle, 960, 250, 1000, 248);
    this.addImage(OTAMA_ASSETS.introSubtitle, 960, 410, 920, 50);
    this.addImage(OTAMA_ASSETS.introFleasLeft, 210, 710, 210, 244).setAngle(-8);
    this.addImage(OTAMA_ASSETS.introFleasRight, 1710, 700, 210, 244).setAngle(8);
    this.createReynaRig(1260, 740, 0.43, {
      messages: REYNA_INTRO_MESSAGES,
      bubbleX: 460,
      bubbleY: -455,
      bubbleScale: 0.82,
    });
    this.createIntroOtto(485, 704);
    this.addButton(OTAMA_ASSETS.introStartButton, 960, 910, 360, 94, () => {
      this.soundSystem.startMenuMusic();
      this.soundSystem.playIntroJingle();
      this.showRegister();
    });
  }

  private addIntroMenuAudioHitArea() {
    const hitArea = this.add
      .rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0xffffff, 0.001)
      .setDepth(-90)
      .setInteractive({ useHandCursor: false });

    hitArea.on(Phaser.Input.Events.POINTER_DOWN, () => {
      if (this.screen !== 'intro') {
        return;
      }

      this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
      void this.soundSystem.unlock();
      this.soundSystem.startMenuMusic();
    });
  }

  private showSelect() {
    this.setScreen('select');
    this.clearScreen();
    useGameStore.getState().setStatus('Seleccion de mascota');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startMenuMusic();
    this.soundSystem.playMenuStinger();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.addImage(OTAMA_ASSETS.selectTitle, 960, 170, 1100, 133);

    const dog = this.addButton(OTAMA_ASSETS.selectDog, 610, 615, 430, 516, () => {
      this.selectedPet = 'dog';
      this.showProduct();
    });
    const cat = this.addButton(OTAMA_ASSETS.selectCat, 1295, 620, 500, 452, () => {
      this.selectedPet = 'cat';
      this.showProduct();
    });

    this.addChoiceLabel(dog.x, 940, `Juega con ${DOG_PRODUCT_NAME}`);
    this.addChoiceLabel(cat.x, 940, 'Juega con Misiprox');
    this.addBackNavButton(() => this.showIntro());
  }

  private showProduct() {
    this.setScreen('product');
    this.clearScreen();
    useGameStore.getState().setStatus(this.selectedPet === 'dog' ? DOG_PRODUCT_NAME : 'Misiprox');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startMenuMusic();
    this.soundSystem.playProductReveal();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.addBackNavButton(() => this.showSelect());
    this.addMenuNavButton();

    if (this.selectedPet === 'dog') {
      this.addImage(OTAMA_ASSETS.dogProductText, 660, 480, 780, 546);
      this.addImage(OTAMA_ASSETS.dogOtto, 1310, 610, 520, 518);
      this.addButton(OTAMA_ASSETS.dogNextButton, 960, 910, 318, 84, () => this.showInstructions());
      return;
    }

    this.addImage(OTAMA_ASSETS.catProductText, 670, 480, 780, 546);
    this.createReynaRig(1310, 705, 0.58, {
      messages: REYNA_PRODUCT_MESSAGES,
      bubbleX: -350,
      bubbleY: -392,
      bubbleScale: 0.88,
    });
    this.addButton(OTAMA_ASSETS.catNextButton, 960, 910, 318, 84, () => this.showInstructions());
  }

  private showInstructions() {
    this.setScreen('instructions');
    this.clearScreen();
    useGameStore.getState().setStatus('Instrucciones');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startMenuMusic();
    this.soundSystem.playInstructionOpen();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.addImage(OTAMA_ASSETS.instructionsTitle, 960, 170, 700, 140);
    this.addImage(OTAMA_ASSETS.instructionsSubtitle, 960, 300, 660, 68);
    this.addImage(OTAMA_ASSETS.instructionsText, 960, 560, 880, 362);
    this.addImage(OTAMA_ASSETS.instructionsFleaLeft, 270, 590, 250, 288).setAngle(-7);
    this.addImage(OTAMA_ASSETS.instructionsFleaRight, 1648, 590, 250, 288).setAngle(7);
    this.addButton(OTAMA_ASSETS.instructionsPlayButton, 960, 910, 318, 84, () => this.showGameplayCountdown());
    this.addBackNavButton(() => this.showProduct());
    this.addMenuNavButton();
  }

  private showGameplayCountdown() {
    this.setScreen('countdown');
    this.clearScreen();
    useGameStore.getState().setStatus('Preparando juego');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.stopAllLoops();

    const background = this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x020604, 1).setDepth(-100);
    const glow = this.add
      .ellipse(WIDTH / 2, HEIGHT / 2, 1080, 620, 0x6ee33f, 0.12)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(-92);
    const title = this.addText(960, 352, 'CARGANDO', 58, '#eaffd8')
      .setOrigin(0.5)
      .setStroke('#0b3f16', 10)
      .setDepth(4);
    const phrase = this.addText(960, 760, 'Toca las pulgas lo más rápido que puedas', 46, '#ffffff')
      .setOrigin(0.5)
      .setAlign('center')
      .setStroke('#0b3f16', 9)
      .setDepth(4)
      .setAlpha(0);
    const countText = this.addText(960, 545, 'UNO', 126, '#ffdf56')
      .setOrigin(0.5)
      .setStroke('#0b3f16', 18)
      .setDepth(5)
      .setAlpha(0);
    const logoGroup = this.add.container(WIDTH / 2, HEIGHT / 2).setDepth(8).setAlpha(0).setScale(0.58);
    const logoHalo = this.add
      .ellipse(0, 18, 1040, 360, 0xffdf56, 0.28)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScale(0.72);
    const logoPlate = this.add.graphics();
    const logoDropShadow = this.add.ellipse(16, 104, 720, 86, 0x000000, 0.58);
    const logoShadow = this.add.image(18, 20, OTAMA_ASSETS.introLogo).setTint(0x001b08).setAlpha(0.72);
    const logoCore = this.add.image(0, 0, OTAMA_ASSETS.introLogo);
    const logoScale = Math.min(820 / logoCore.width, 260 / logoCore.height);
    const outlineOffsets = [
      [-12, 0],
      [12, 0],
      [0, -12],
      [0, 12],
      [-9, -9],
      [9, -9],
      [-9, 9],
      [9, 9],
    ];
    const logoOutline = outlineOffsets.map(([offsetX, offsetY]) =>
      this.add
        .image(offsetX, offsetY, OTAMA_ASSETS.introLogo)
        .setTint(0xffdf56)
        .setAlpha(0.95)
        .setScale(logoScale * 1.018),
    );
    const logoWhiteEdge = outlineOffsets.map(([offsetX, offsetY]) =>
      this.add
        .image(offsetX * 0.52, offsetY * 0.52, OTAMA_ASSETS.introLogo)
        .setTint(0xffffff)
        .setAlpha(0.78)
        .setScale(logoScale * 1.008),
    );
    const logoShine = this.add
      .rectangle(-480, 0, 84, 330, 0xffffff, 0)
      .setAngle(-18)
      .setBlendMode(Phaser.BlendModes.ADD);
    const loadingObjects: Phaser.GameObjects.GameObject[] = [title, phrase, countText];

    logoPlate.fillStyle(0xffffff, 0.12);
    logoPlate.fillRoundedRect(-480, -155, 960, 310, 62);
    logoPlate.lineStyle(7, 0xffdf56, 0.8);
    logoPlate.strokeRoundedRect(-480, -155, 960, 310, 62);
    logoPlate.setBlendMode(Phaser.BlendModes.ADD);
    logoShadow.setScale(logoScale * 1.04);
    logoCore.setScale(logoScale);
    logoGroup.add([logoHalo, logoDropShadow, logoPlate, logoShadow, ...logoOutline, ...logoWhiteEdge, logoCore, logoShine]);

    this.tweens.add({
      targets: background,
      fillAlpha: 0.92,
      duration: 360,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: glow,
      alpha: 0.28,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 720,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: title,
      alpha: 0.72,
      scaleX: 1.04,
      scaleY: 1.04,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: phrase,
      alpha: 1,
      y: phrase.y - 14,
      duration: 520,
      delay: 280,
      ease: 'Cubic.easeOut',
    });

    const blockColors = [0xffdf56, 0x6ee33f, 0x38bdf8, 0xffffff, 0xff9800];

    for (let index = 0; index < 26; index += 1) {
      const block = this.add
        .rectangle(
          380 + (index % 13) * 96,
          900 + Math.floor(index / 13) * 58,
          66,
          32,
          Phaser.Math.RND.pick(blockColors),
          0.16,
        )
        .setStrokeStyle(3, 0xffffff, 0.16)
        .setDepth(3);

      loadingObjects.push(block);
      this.tweens.add({
        targets: block,
        y: block.y - Phaser.Math.Between(18, 44),
        alpha: 0.92,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 220,
        delay: 90 + index * 36,
        yoyo: true,
        repeat: -1,
        repeatDelay: 520,
        ease: 'Linear',
      });
    }

    for (let index = 0; index < 18; index += 1) {
      const sparkle = this.add
        .rectangle(
          Phaser.Math.Between(220, WIDTH - 220),
          Phaser.Math.Between(150, HEIGHT - 150),
          Phaser.Math.Between(8, 18),
          Phaser.Math.Between(8, 18),
          Phaser.Math.RND.pick(blockColors),
          0,
        )
        .setAngle(Phaser.Math.Between(0, 45))
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(2);

      loadingObjects.push(sparkle);
      this.tweens.add({
        targets: sparkle,
        alpha: 0.68,
        y: sparkle.y + Phaser.Math.Between(28, 88),
        angle: sparkle.angle + 90,
        duration: Phaser.Math.Between(620, 1100),
        delay: Phaser.Math.Between(0, 900),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    ['UNO', 'DOS', 'TRES'].forEach((label, index) => {
      this.time.delayedCall(280 + index * 720, () => {
        if (this.screen !== 'countdown') {
          return;
        }

        this.soundSystem.playTap();
        countText.setText(label).setAlpha(0).setScale(0.5).setAngle(index % 2 === 0 ? -3 : 3);
        this.tweens.add({
          targets: countText,
          alpha: 1,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
          duration: 190,
          yoyo: true,
          hold: 300,
          ease: 'Back.easeOut',
        });
      });
    });

    this.time.delayedCall(2500, () => {
      if (this.screen !== 'countdown') {
        return;
      }

      this.soundSystem.playIntroJingle();
      this.tweens.add({
        targets: loadingObjects,
        alpha: 0,
        y: '-=34',
        duration: 260,
        ease: 'Cubic.easeIn',
      });
      this.tweens.add({
        targets: glow,
        alpha: 0.48,
        scaleX: 1.28,
        scaleY: 1.28,
        duration: 520,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: logoGroup,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 460,
        ease: 'Back.easeOut',
      });
      this.tweens.add({
        targets: logoGroup,
        y: HEIGHT / 2 - 10,
        duration: 520,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: logoHalo,
        alpha: 0.48,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 440,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: logoShine,
        x: 500,
        alpha: { from: 0, to: 0.86 },
        duration: 680,
        delay: 180,
        ease: 'Cubic.easeInOut',
      });
    });

    this.time.delayedCall(4250, () => {
      if (this.screen === 'countdown') {
        this.startRun();
      }
    });
  }

  private startRun() {
    this.setScreen('playing');
    this.clearScreen();
    this.score = 0;
    this.hairTouches = 0;
    this.elapsedMs = 0;
    this.runDurationMs = gameSettings.gameplay.durationSeconds * 1000;
    this.lastRunEndedByLoss = false;
    this.currentRunSavedToRanking = false;
    this.acceptGameplayInputAtMs = this.time.now + 220;
    this.fleaKillTimestamps.splice(0);
    this.frenzyUntilMs = 0;
    this.frenzyLevel = 0;
    this.tickBossSpawned = false;
    this.tickBossWarningStarted = false;
    this.tickBoss = undefined;
    useGameStore.getState().setStatus('Eliminando pulgas');

    this.addBackground(this.selectedPet === 'dog' ? OTAMA_ASSETS.dogFur : OTAMA_ASSETS.catFur);
    this.addGameplayHud();
    this.addMenuNavButton('Menú');
    this.spawnFlea();
    this.scheduleNextFlea();
    this.scheduleHairCue();
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.unlock();
    this.soundSystem.playGameStart();
    this.soundSystem.startGameplayMusic();
    this.publishRuntimeState();
  }

  private endRun() {
    if (this.screen !== 'playing') {
      return;
    }

    this.clearGameplayTimers();
    this.fleas.splice(0).forEach((flea) => this.destroyFlea(flea));
    this.soundSystem.stopAllLoops();
    this.lastRunEndedByLoss = this.hairTouches >= gameSettings.gameplay.maxHairTouches;
    if (this.selectedPet === 'dog' && this.lastRunEndedByLoss) {
      this.showLoss();
      return;
    }

    this.showScore();
  }

  private showScore() {
    this.setScreen('score');
    this.clearScreen();
    useGameStore.getState().setStatus(`Puntaje final: ${this.score}`);
    const isNewLeader = this.isNewLeaderScore();
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playScorePanel();

    if (isNewLeader) {
      this.soundSystem.playWinJingle();
    }
    this.soundSystem.startScoreMusic(isNewLeader);

    const assets =
      this.selectedPet === 'dog'
        ? {
            bg: OTAMA_ASSETS.dogScoreBg,
            title: OTAMA_ASSETS.dogScoreTitle,
            subtitle: OTAMA_ASSETS.dogScoreSubtitle,
            product: OTAMA_ASSETS.dogScoreProduct,
            character: OTAMA_ASSETS.dogScoreCharacter,
            label: OTAMA_ASSETS.dogScoreLabel,
            button: OTAMA_ASSETS.dogScoreRankingButton,
          }
        : {
            bg: OTAMA_ASSETS.catScoreBg,
            title: OTAMA_ASSETS.catScoreTitle,
            subtitle: OTAMA_ASSETS.catScoreSubtitle,
            product: OTAMA_ASSETS.catScoreProduct,
            character: OTAMA_ASSETS.catScoreCharacter,
            label: OTAMA_ASSETS.catScoreLabel,
            button: OTAMA_ASSETS.catScoreRankingButton,
          };

    this.addBackground(assets.bg);
    this.addScoreSpotlights();
    this.addScoreConfetti(isNewLeader ? 110 : 72);
    this.addImage(assets.title, 960, 126, 620, 154).setDepth(6);
    if (isNewLeader) {
      this.addText(960, 250, '¡Lo lograste, eres el líder!', 54, '#ffffff').setOrigin(0.5).setStroke('#21411f', 8).setDepth(6);
    } else {
      this.addImage(assets.subtitle, 960, 250, 650, 60).setDepth(6);
    }
    this.addScoreProductShowcase(assets.product);
    this.addScorePanel(assets.label, isNewLeader);

    if (this.selectedPet === 'dog' && isNewLeader) {
      this.addLeaderOttoCelebration(1480, 650);
    } else {
      const character = this.addImage(assets.character, 1510, 690, 500, 422).setDepth(5);

      this.tweens.add({
        targets: character,
        y: character.y - 12,
        duration: 1550,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.addButton(assets.button, 960, 922, 438, 82, () => this.saveEntryAndShowRanking()).setDepth(16);
    this.addRetryNavButton(() => this.showGameplayCountdown());
    this.addMenuNavButton();
  }

  private isNewLeaderScore() {
    const topScore = this.readRanking().reduce((highest, entry) => Math.max(highest, entry.score), 0);

    return this.score > topScore;
  }

  private addScoreSpotlights() {
    const centerGlow = this.add.ellipse(960, 520, 820, 430, 0xffffff, 0.13).setDepth(-10).setBlendMode(Phaser.BlendModes.ADD);
    const productGlow = this.add.ellipse(345, 675, 500, 270, 0xffffff, 0.18).setDepth(-8).setBlendMode(Phaser.BlendModes.ADD);
    const characterGlow = this.add.ellipse(1480, 650, 520, 470, 0xffdf56, 0.16).setDepth(-8).setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: [centerGlow, productGlow, characterGlow],
      alpha: 0.32,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 1250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private addScoreConfetti(amount: number) {
    const colors = [0xffdf56, 0xffffff, 0x36d399, 0x38bdf8, 0xf472b6, 0xfb923c, 0xa78bfa];

    for (let index = 0; index < amount; index += 1) {
      const color = Phaser.Math.RND.pick(colors);
      const x = Phaser.Math.Between(-80, WIDTH + 80);
      const y = Phaser.Math.Between(-HEIGHT, HEIGHT);
      const size = Phaser.Math.Between(7, 18);
      const alpha = Phaser.Math.FloatBetween(0.58, 0.96);
      const piece =
        index % 4 === 0
          ? this.add.star(x, y, 5, size * 0.38, size, color, alpha)
          : index % 4 === 1
            ? this.add.triangle(x, y, 0, size, size * 0.5, 0, size, size, color, alpha)
            : this.add.rectangle(x, y, size * 1.45, size * 0.48, color, alpha);

      piece.setDepth(20).setAngle(Phaser.Math.Between(0, 360));

      this.tweens.add({
        targets: piece,
        x: x + Phaser.Math.Between(-160, 160),
        y: HEIGHT + Phaser.Math.Between(40, 220),
        angle: piece.angle + Phaser.Math.Between(240, 900),
        duration: Phaser.Math.Between(3600, 7800),
        delay: Phaser.Math.Between(0, 2200),
        repeat: -1,
        ease: 'Linear',
        onRepeat: () => {
          piece.setPosition(Phaser.Math.Between(-80, WIDTH + 80), Phaser.Math.Between(-240, -20));
          piece.setAlpha(Phaser.Math.FloatBetween(0.58, 0.96));
        },
      });
    }

    for (let index = 0; index < 34; index += 1) {
      const sparkle = this.add
        .circle(960 + Phaser.Math.Between(-360, 360), 500 + Phaser.Math.Between(-150, 170), Phaser.Math.Between(3, 8), Phaser.Math.RND.pick(colors), 0.82)
        .setDepth(18)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.tweens.add({
        targets: sparkle,
        alpha: 0,
        scaleX: 2.6,
        scaleY: 2.6,
        y: sparkle.y - Phaser.Math.Between(60, 180),
        duration: Phaser.Math.Between(850, 1550),
        delay: Phaser.Math.Between(0, 900),
        repeat: -1,
        ease: 'Cubic.easeOut',
      });
    }
  }

  private addScoreProductShowcase(productKey: OtamaAssetKey) {
    const pedestal = this.add.ellipse(345, 805, 360, 58, 0x073f7a, 0.16).setDepth(3);
    const product = this.addImage(productKey, 345, 650, 430, 326).setDepth(5);

    this.tweens.add({
      targets: product,
      y: product.y - 14,
      angle: -2,
      duration: 1450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: pedestal,
      scaleX: 1.08,
      alpha: 0.25,
      duration: 1450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private addScorePanel(labelKey: OtamaAssetKey, isNewLeader: boolean) {
    const panel = this.add.container(960, 535).setDepth(12).setAlpha(0);
    const graphics = this.add.graphics();
    const label = this.add.image(-118, -64, labelKey);
    const labelScale = Math.min(292 / label.width, 64 / label.height);
    const scoreText = this.addText(126, -50, `${this.score}`, 132, '#ffffff').setOrigin(0.5).setStroke('#21411f', 12);
    const promptText = this.addText(0, 82, isNewLeader ? '¡Nuevo líder de Nexus Max 3M!' : '¿Podrás superar al líder?', 42, '#24451f')
      .setOrigin(0.5)
      .setStroke('#ffffff', 6);

    graphics.fillStyle(0xffffff, 0.2);
    graphics.fillRoundedRect(-390, -132, 780, 284, 46);
    graphics.lineStyle(5, isNewLeader ? 0xffdf56 : 0xffffff, 0.72);
    graphics.strokeRoundedRect(-390, -132, 780, 284, 46);
    label.setScale(labelScale);
    panel.add([graphics, label, scoreText, promptText]);

    if (isNewLeader) {
      const badge = this.add.container(0, -164);
      const badgeBg = this.add.graphics();
      const badgeText = this.addText(0, 0, 'PRIMER LUGAR', 34, '#ffffff').setOrigin(0.5).setStroke('#21411f', 7);

      badgeBg.fillStyle(0xff8f00, 1);
      badgeBg.fillRoundedRect(-170, -36, 340, 72, 30);
      badgeBg.lineStyle(5, 0xffffff, 1);
      badgeBg.strokeRoundedRect(-170, -36, 340, 72, 30);
      badge.add([badgeBg, badgeText]);
      panel.add(badge);

      this.tweens.add({
        targets: badge,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 480,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.tweens.add({
      targets: panel,
      alpha: 1,
      y: panel.y - 10,
      duration: 520,
      ease: 'Back.easeOut',
    });
  }

  private addLeaderOttoCelebration(x: number, y: number) {
    const root = this.add.container(x, y).setDepth(14).setAlpha(0);
    const aura = this.add.ellipse(0, -30, 510, 500, 0xffdf56, 0.18).setBlendMode(Phaser.BlendModes.ADD);
    const otto = this.add.container(0, 0).setScale(0.82);
    const shadow = this.add.ellipse(0, 210, 330, 54, 0x073f7a, 0.16);
    const body = this.add.image(0, 72, OTAMA_ASSETS.ottoBody).setOrigin(0.5).setScale(0.68, 0.52);
    const leftPaw = this.add.image(-130, -42, OTAMA_ASSETS.ottoPaw1).setOrigin(0.78, 0.86).setScale(0.48).setAngle(-40);
    const rightPaw = this.add.image(130, -40, OTAMA_ASSETS.ottoPaw2).setOrigin(0.22, 0.86).setScale(0.48).setAngle(40);
    const leftEar = this.add.image(-106, -148, OTAMA_ASSETS.ottoEar1).setOrigin(0.76, 0.38).setAngle(-3);
    const rightEar = this.add.image(106, -148, OTAMA_ASSETS.ottoEar2).setOrigin(0.24, 0.38).setAngle(3);
    const head = this.add.image(0, -124, OTAMA_ASSETS.ottoHead).setOrigin(0.5).setScale(1.04);
    const leftBrow = this.add.image(-50, -204, OTAMA_ASSETS.ottoBrow1).setOrigin(0.5).setScale(0.96).setAngle(-12);
    const rightBrow = this.add.image(50, -204, OTAMA_ASSETS.ottoBrow2).setOrigin(0.5).setScale(0.96).setAngle(12);
    const leftEye = this.add.image(-38, -162, OTAMA_ASSETS.ottoEye1).setOrigin(0.5).setScale(1.18);
    const rightEye = this.add.image(38, -162, OTAMA_ASSETS.ottoEye2).setOrigin(0.5).setScale(1.18);
    const face = this.add.image(0, -102, OTAMA_ASSETS.ottoNoseMouth).setOrigin(0.5).setScale(1.12);
    const scarf = this.add.image(-86, -36, OTAMA_ASSETS.ottoScarf).setOrigin(0.22, 0.44).setScale(0.78, 0.58).setAngle(-2);
    const box = this.add.image(42, 120, OTAMA_ASSETS.dogScoreProduct);
    const boxScale = Math.min(210 / box.width, 156 / box.height);

    box.setScale(boxScale).setAngle(-8).setDepth(2);
    otto.add([shadow, body, leftPaw, rightPaw, leftEar, rightEar, head, leftBrow, rightBrow, leftEye, rightEye, face, scarf]);
    root.add([aura, otto, box]);

    for (let index = 0; index < 12; index += 1) {
      const star = this.add
        .star(Phaser.Math.Between(-210, 210), Phaser.Math.Between(-260, 190), 5, 7, 18, Phaser.Math.RND.pick([0xffdf56, 0xffffff, 0x38bdf8]), 0.92)
        .setBlendMode(Phaser.BlendModes.ADD);

      root.add(star);
      this.tweens.add({
        targets: star,
        angle: 360,
        scaleX: 0.35,
        scaleY: 0.35,
        alpha: 0.1,
        duration: Phaser.Math.Between(820, 1400),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.tweens.add({
      targets: root,
      alpha: 1,
      y: y - 18,
      duration: 560,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: otto,
      angle: 9,
      x: 18,
      scaleX: 0.88,
      scaleY: 0.86,
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [root, aura],
      y: '-=18',
      duration: 980,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: box,
      angle: 14,
      scaleX: boxScale * 1.08,
      scaleY: boxScale * 1.08,
      y: box.y - 18,
      duration: 540,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftPaw, rightPaw],
      angle: '+=16',
      duration: 360,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftEar, rightEar, scarf],
      angle: '+=8',
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private showLoss() {
    this.setScreen('loss');
    this.clearScreen();
    useGameStore.getState().setStatus('Intentalo otra vez');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playLoseJingle();
    this.soundSystem.startLossMusic();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x0c5b1c, 0.18).setDepth(-90);
    this.addLossSpeechBubble(960, 220);
    this.addSadLossOtto(960, 695);

    const retryHitArea = this.add
      .rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(30);
    retryHitArea.on(Phaser.Input.Events.POINTER_DOWN, () => {
      if (this.screen !== 'loss') {
        return;
      }

      this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
      this.soundSystem.unlock();
      this.soundSystem.playTap();
      this.showGameplayCountdown();
    });
    this.addRetryNavButton(() => this.showGameplayCountdown());
    this.addMenuNavButton();
  }

  private addLossSpeechBubble(x: number, y: number) {
    const container = this.add.container(x, y).setAlpha(0).setScale(0.88);
    const graphics = this.add.graphics();
    const glow = this.add.ellipse(0, 0, 700, 205, 0xffffff, 0.18).setBlendMode(Phaser.BlendModes.ADD);
    const text = this.addText(0, -6, 'Inténtalo otra vez', 74, '#24451f')
      .setOrigin(0.5)
      .setStroke('#ffffff', 12);

    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillRoundedRect(-360, -82, 720, 164, 42);
    graphics.lineStyle(8, 0xffdf56, 1);
    graphics.strokeRoundedRect(-360, -82, 720, 164, 42);
    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillTriangle(-70, 72, 0, 128, 38, 72);
    graphics.lineStyle(7, 0xffdf56, 1);
    graphics.lineBetween(-70, 72, 0, 128);
    graphics.lineBetween(0, 128, 38, 72);
    container.add([glow, graphics, text]);

    this.tweens.add({
      targets: container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 420,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: glow,
      alpha: 0.34,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 1150,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private addSadLossOtto(x: number, y: number) {
    const root = this.add.container(x, y).setScale(1.08).setAlpha(0).setDepth(4);
    const shadow = this.add.ellipse(0, 222, 330, 50, 0x24451f, 0.18);
    const leftArm = this.add.image(-88, -6, OTAMA_ASSETS.ottoPaw1).setOrigin(0.88, 0.9).setScale(0.48).setAngle(-132);
    const rightArm = this.add.image(88, -6, OTAMA_ASSETS.ottoPaw2).setOrigin(0.12, 0.9).setScale(0.48).setAngle(132);
    const body = this.add.image(0, 80, OTAMA_ASSETS.ottoBody).setOrigin(0.5).setScale(0.74, 0.54);
    const leftEar = this.add.image(-104, -162, OTAMA_ASSETS.ottoEar1).setOrigin(0.76, 0.38).setAngle(-10);
    const rightEar = this.add.image(104, -162, OTAMA_ASSETS.ottoEar2).setOrigin(0.24, 0.38).setAngle(10);
    const head = this.add.image(0, -132, OTAMA_ASSETS.ottoHead).setOrigin(0.5).setScale(1.05);
    const leftEye = this.add
      .image(-42, -170, OTAMA_ASSETS.ottoSadEyeLeft)
      .setOrigin(0.5)
      .setDisplaySize(67, 70)
      .setAngle(-5);
    const rightEye = this.add
      .image(42, -170, OTAMA_ASSETS.ottoSadEyeRight)
      .setOrigin(0.5)
      .setDisplaySize(65, 68)
      .setAngle(5);
    const leftWhitePupil = this.add
      .ellipse(-30, -158, 13, 16, 0xffffff, 0.95)
      .setAngle(-14)
      .setBlendMode(Phaser.BlendModes.ADD);
    const rightWhitePupil = this.add
      .ellipse(54, -158, 13, 16, 0xffffff, 0.95)
      .setAngle(14)
      .setBlendMode(Phaser.BlendModes.ADD);
    const leftTearGlint = this.add.circle(-52, -148, 4.5, 0xffffff, 0.72).setBlendMode(Phaser.BlendModes.ADD);
    const rightTearGlint = this.add.circle(30, -148, 4.5, 0xffffff, 0.72).setBlendMode(Phaser.BlendModes.ADD);
    const leftLid = this.add.ellipse(-42, -195, 78, 34, 0xffdf56, 0.88).setAngle(-9);
    const rightLid = this.add.ellipse(42, -195, 78, 34, 0xffdf56, 0.88).setAngle(9);
    const leftBrow = this.add.image(-53, -220, OTAMA_ASSETS.ottoBrow1).setOrigin(0.5).setScale(1.16).setAngle(20);
    const rightBrow = this.add.image(53, -220, OTAMA_ASSETS.ottoBrow2).setOrigin(0.5).setScale(1.16).setAngle(-20);
    const muzzle = this.createSadMuzzle();
    muzzle.setPosition(0, -104);
    const scarf = this.add.image(-76, 18, OTAMA_ASSETS.ottoScarf).setOrigin(0.22, 0.44).setScale(0.78, 0.56).setAngle(-1);

    root.add([
      shadow,
      leftArm,
      rightArm,
      body,
      leftEar,
      rightEar,
      head,
      leftEye,
      rightEye,
      leftWhitePupil,
      rightWhitePupil,
      leftTearGlint,
      rightTearGlint,
      leftLid,
      rightLid,
      leftBrow,
      rightBrow,
      muzzle,
      scarf,
    ]);

    this.tweens.add({
      targets: root,
      alpha: 1,
      y: y - 14,
      duration: 520,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: [leftEye, rightEye, leftWhitePupil, rightWhitePupil, leftTearGlint, rightTearGlint],
      y: '+=3',
      duration: 1450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftEye, rightEye, leftWhitePupil, rightWhitePupil, leftTearGlint, rightTearGlint],
      x: '+=1.4',
      duration: 62,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftWhitePupil, rightWhitePupil, leftTearGlint, rightTearGlint],
      alpha: 0.34,
      scaleX: 1.18,
      scaleY: 1.18,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftBrow, rightBrow],
      y: '-=4',
      duration: 1300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: scarf,
      angle: 3,
      scaleX: 0.84,
      scaleY: 0.6,
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftLid, rightLid],
      y: '+=2',
      alpha: 0.72,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const blink = () => {
      if (this.screen !== 'loss' || !leftEye.active || !rightEye.active) {
        return;
      }

      this.tweens.add({
        targets: [leftEye, rightEye, leftWhitePupil, rightWhitePupil, leftTearGlint, rightTearGlint],
        scaleY: 0.015,
        duration: 90,
        yoyo: true,
        ease: 'Quad.easeInOut',
        onComplete: () => this.time.delayedCall(Phaser.Math.Between(2600, 4300), blink),
      });
    };

    this.time.delayedCall(1500, blink);
  }

  private createSadMuzzle() {
    const muzzle = this.add.graphics();

    muzzle.fillStyle(0x24451f, 1);
    muzzle.fillEllipse(0, 6, 42, 30);
    muzzle.fillTriangle(-18, 16, 18, 16, 0, 36);
    muzzle.fillStyle(0xffffff, 0.26);
    muzzle.fillEllipse(-8, -2, 10, 6);
    muzzle.lineStyle(7, 0x24451f, 1);
    muzzle.beginPath();
    muzzle.moveTo(0, 30);
    muzzle.lineTo(0, 52);
    muzzle.strokePath();
    muzzle.beginPath();
    const frown = new Phaser.Curves.QuadraticBezier(
      new Phaser.Math.Vector2(-42, 70),
      new Phaser.Math.Vector2(0, 42),
      new Phaser.Math.Vector2(42, 70),
    );
    const frownPoints = frown.getPoints(18);
    muzzle.moveTo(frownPoints[0].x, frownPoints[0].y);
    frownPoints.slice(1).forEach((point) => muzzle.lineTo(point.x, point.y));
    muzzle.strokePath();

    return muzzle;
  }

  private showRegister() {
    this.setScreen('register');
    this.clearScreen();
    this.ottoSpeechMessages = OTTO_REGISTER_MESSAGES;
    useGameStore.getState().setStatus('Registro de cliente potencial');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startMenuMusic();
    this.soundSystem.playRegisterOpen();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x14360f, 0.2).setDepth(-82);
    this.addRegisterStageFx();
    this.addImage(OTAMA_ASSETS.registerLogo, 960, 104, 372, 116).setDepth(12);
    this.addRegisterOttoPitch();

    const panelX = 1238;
    const panelY = 565;
    const panel = this.add.container(panelX, panelY).setDepth(20).setAlpha(0);
    const panelBg = this.add.graphics();
    const glow = this.add.ellipse(0, 0, 940, 700, 0xffdf56, 0.18).setBlendMode(Phaser.BlendModes.ADD);
    const title = this.addText(0, -258, 'Registrate', 76, '#24451f').setOrigin(0.5).setStroke('#ffffff', 10);
    const subtitle = this.addText(0, -188, 'Activa tu partida y participa por premios OTAMA', 29, '#2e6425')
      .setOrigin(0.5)
      .setStroke('#ffffff', 5);
    const badge = this.add.container(0, -128);
    const badgeBg = this.add.graphics();
    const badgeText = this.addText(0, -1, '¿Podrás superar al líder?', 26, '#ffffff').setOrigin(0.5);
    const nameLabel = this.addText(-310, -90, 'Nombre y apellido', 30, '#24451f').setOrigin(0, 0.5);
    const contactLabel = this.addText(-310, 80, 'Correo o telefono', 30, '#24451f').setOrigin(0, 0.5);
    const hint = this.addText(0, 236, 'Tus datos se usaran para contactarte y registrar tu participacion.', 24, '#496f38')
      .setOrigin(0.5)
      .setAlign('center')
      .setWordWrapWidth(650);

    panelBg.fillStyle(0xffffff, 0.9);
    panelBg.fillRoundedRect(-440, -330, 880, 660, 48);
    panelBg.lineStyle(8, 0xffdf56, 1);
    panelBg.strokeRoundedRect(-440, -330, 880, 660, 48);
    badgeBg.fillStyle(0xff9800, 0.96);
    badgeBg.fillRoundedRect(-305, -26, 610, 52, 26);
    badgeBg.lineStyle(4, 0xffffff, 0.95);
    badgeBg.strokeRoundedRect(-305, -26, 610, 52, 26);
    badge.add([badgeBg, badgeText]);
    panel.add([glow, panelBg, title, subtitle, badge, nameLabel, contactLabel, hint]);

    this.nameInput = this.addInput(panelX, 535, 'Nombre y apellido').setDepth(32);
    this.contactInput = this.addInput(panelX, 705, 'Correo o telefono').setDepth(32);
    this.addNavButton('Continuar', panelX, 930, () => this.saveLeadAndShowSelect(hint), {
      tone: 'primary',
      width: 360,
      depth: 33,
    });
    this.addBackNavButton(() => this.showIntro()).setDepth(34);

    this.tweens.add({
      targets: panel,
      alpha: 1,
      y: panel.y - 12,
      duration: 420,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: glow,
      alpha: 0.26,
      scaleX: 1.06,
      scaleY: 1.06,
      duration: 1150,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: badge,
      scaleX: 1.04,
      scaleY: 1.04,
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private addRegisterStageFx() {
    const colors = [0xffdf56, 0xffffff, 0x7bd83e, 0xff9800, 0x38bdf8];

    for (let index = 0; index < 5; index += 1) {
      const beam = this.add.graphics().setDepth(-45).setAlpha(0.12);
      const startX = 160 + index * 390;
      const color = index % 2 === 0 ? 0xffffff : 0xffdf56;

      beam.fillStyle(color, 1);
      beam.fillTriangle(startX - 170, HEIGHT, startX + 80, HEIGHT, 980 + index * 70, 0);
      beam.setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: beam,
        alpha: 0.28,
        x: index % 2 === 0 ? 40 : -40,
        duration: 1600 + index * 170,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    for (let index = 0; index < 62; index += 1) {
      const isCircle = index % 3 === 0;
      const color = Phaser.Math.RND.pick(colors);
      const x = Phaser.Math.Between(60, WIDTH - 60);
      const y = Phaser.Math.Between(190, HEIGHT - 80);
      const sparkle = isCircle
        ? this.add.circle(x, y, Phaser.Math.Between(4, 10), color, 0.7)
        : this.add.rectangle(x, y, Phaser.Math.Between(10, 24), Phaser.Math.Between(5, 14), color, 0.72).setAngle(Phaser.Math.Between(-20, 20));

      sparkle.setDepth(-8).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: sparkle,
        y: sparkle.y - Phaser.Math.Between(45, 130),
        x: sparkle.x + Phaser.Math.Between(-26, 26),
        alpha: 0.08,
        angle: Phaser.Math.Between(-180, 180),
        duration: Phaser.Math.Between(1600, 3400),
        delay: Phaser.Math.Between(0, 900),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    const runway = this.add.ellipse(450, 984, 650, 90, 0x123f16, 0.2).setDepth(-4);
    const pulse = this.add.ellipse(450, 984, 410, 48, 0xffdf56, 0.16).setDepth(-3).setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: [runway, pulse],
      scaleX: 1.12,
      alpha: 0.32,
      duration: 920,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private addRegisterOttoPitch() {
    const headline = this.addText(395, 244, 'OTTO te invita', 54, '#ffffff')
      .setOrigin(0.5)
      .setStroke('#24451f', 9)
      .setDepth(10);
    const subline = this.addText(395, 308, 'Completa tus datos\ny empieza el reto', 34, '#ffdf56')
      .setOrigin(0.5)
      .setAlign('center')
      .setStroke('#24451f', 7)
      .setDepth(10);
    const arrow = this.add.triangle(690, 510, 0, -34, 62, 0, 0, 34, 0xffdf56, 0.88).setDepth(10).setBlendMode(Phaser.BlendModes.ADD);

    this.createIntroOtto(390, 790, {
      messages: OTTO_REGISTER_MESSAGES,
      bubbleX: 220,
      bubbleY: -318,
      depth: 10,
      initialScale: 0.68,
      targetScale: 0.78,
    });

    this.tweens.add({
      targets: [headline, subline],
      y: '+=10',
      duration: 1050,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: arrow,
      x: '+=30',
      scaleX: 1.24,
      scaleY: 1.24,
      alpha: 0.34,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private showRanking() {
    this.setScreen('ranking');
    this.clearScreen();
    useGameStore.getState().setStatus('Ranking');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startScoreTableMusic();

    this.addBackground(OTAMA_ASSETS.commonBg);
    this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x24451f, 0.08).setDepth(-80);
    this.addImage(OTAMA_ASSETS.rankingLogo, 230, 110, 300, 94).setDepth(4);
    this.addImage(OTAMA_ASSETS.rankingFireworksLeft, 260, 520, 300, 320).setAlpha(0.65).setDepth(3);
    this.addImage(OTAMA_ASSETS.rankingFireworksRight, 1620, 500, 290, 354).setAlpha(0.65).setDepth(3);
    this.addImage(OTAMA_ASSETS.rankingMisiprox, 390, 875, 285, 198).setDepth(4);
    this.addImage(OTAMA_ASSETS.rankingNexus, 1530, 875, 285, 198).setDepth(4);
    this.addRankingBoard();
    this.addButton(OTAMA_ASSETS.rankingMenuButton, 960, 950, 438, 82, () => this.showIntro()).setDepth(12);
    this.addBackNavButton(() => this.showScore());
    this.addMenuNavButton();
  }

  private addRankingBoard() {
    const entries = this.readRanking()
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
    const medalKeys = [OTAMA_ASSETS.rankingPosition1, OTAMA_ASSETS.rankingPosition2, OTAMA_ASSETS.rankingPosition3];
    const board = this.add.container(960, 475).setDepth(8).setAlpha(0);
    const boardBg = this.add.graphics();
    const title = this.addText(0, -282, 'Clasificación', 68, '#24451f').setOrigin(0.5).setStroke('#ffffff', 9);
    const subtitle = this.addText(0, -222, 'Los mejores cazadores de pulgas', 30, '#496f38').setOrigin(0.5);

    boardBg.fillStyle(0xffffff, 0.78);
    boardBg.fillRoundedRect(-475, -330, 950, 660, 48);
    boardBg.lineStyle(8, 0xffdf56, 1);
    boardBg.strokeRoundedRect(-475, -330, 950, 660, 48);
    board.add([boardBg, title, subtitle]);

    entries.forEach((entry, index) => {
      const y = -140 + index * 96;
      const row = this.add.graphics();

      row.fillStyle(index === 0 ? 0xffdf56 : 0xffffff, index === 0 ? 0.28 : 0.36);
      row.fillRoundedRect(-400, y - 42, 800, 84, 24);
      row.lineStyle(3, index === 0 ? 0xffdf56 : 0xffffff, index === 0 ? 0.95 : 0.35);
      row.strokeRoundedRect(-400, y - 42, 800, 84, 24);
      board.add(row);

      const medalOrNumber = index < medalKeys.length ? this.add.image(-340, y, medalKeys[index]) : this.addText(-340, y, `${index + 1}`, 48, '#24451f').setOrigin(0.5);
      if (index < medalKeys.length) {
        const medal = medalOrNumber as Phaser.GameObjects.Image;
        medal.setScale(Math.min(58 / medal.width, 78 / medal.height));
      }

      const name = this.addText(-270, y - 14, entry.name, 38, '#24451f').setOrigin(0, 0.5);
      const product = this.addText(-270, y + 25, entry.pet === 'dog' ? DOG_PRODUCT_NAME : 'Misiprox', 23, '#496f38').setOrigin(0, 0.5);
      const score = this.addText(340, y, `${entry.score} pts`, 44, '#24451f').setOrigin(1, 0.5);

      board.add([medalOrNumber, name, product, score]);
    });

    this.tweens.add({
      targets: board,
      alpha: 1,
      y: board.y - 12,
      duration: 460,
      ease: 'Back.easeOut',
    });
  }

  private handlePointerDown = (pointer: Phaser.Input.Pointer) => {
    if (this.screen !== 'playing') {
      return;
    }

    if (this.time.now < this.acceptGameplayInputAtMs) {
      return;
    }

    if (pointer.id === this.lastFleaHitPointerId && this.time.now - this.lastFleaHitAtMs < 120) {
      return;
    }

    const point = this.getPointerWorldPoint(pointer);

    if (point.y < GAMEPLAY_INPUT_TOP_SAFE_Y) {
      return;
    }

    const hitBonus = this.bonusCollectibles.find((bonus) => bonus.hitZone.getBounds().contains(point.x, point.y));

    if (hitBonus) {
      this.collectBonus(hitBonus);
      return;
    }

    if (this.tickBoss?.hitZone.getBounds().contains(point.x, point.y)) {
      this.hitTickBoss(point.x, point.y);
      return;
    }

    const hitMiniTick = this.miniTicks.find((miniTick) => miniTick.hitZone.getBounds().contains(point.x, point.y));

    if (hitMiniTick) {
      this.collectMiniTick(hitMiniTick, point.x, point.y);
      return;
    }

    const hitFlea = this.fleas.find((flea) => flea.hitZone.getBounds().contains(point.x, point.y));

    if (hitFlea) {
      this.collectFlea(hitFlea, point.x, point.y);
      return;
    }

    this.registerHairTouch(point.x, point.y);
  };

  private collectFlea(flea: FleaTarget, x: number, y: number) {
    if (!flea.sprite.active || !this.fleas.includes(flea)) {
      return;
    }

    const killsInWindow = this.recordFleaKill();
    const isFrenzy = this.isFrenzyActive();
    const scoreGain =
      gameSettings.gameplay.scorePerFlea * (isFrenzy ? gameSettings.gameplay.frenzy.scoreMultiplier : 1);

    this.score += scoreGain;
    this.removeFlea(flea);
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playReward();
    this.particleFx.burst(x, y, isFrenzy ? 0xff8f00 : 0xffdf56, isFrenzy ? 26 : 12);
    this.showFloatingScore(x, y, scoreGain, isFrenzy, killsInWindow);
    useGameStore.getState().registerTap({ reward: true });
    this.updateGameplayHud();
  }

  private registerHairTouch(x: number, y: number) {
    this.score = Math.max(0, this.score - gameSettings.gameplay.hairPenalty);
    this.hairTouches += 1;
    this.fleaKillTimestamps.splice(0);
    this.cancelFrenzyOnPenalty();
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playPenalty();
    this.flashPenalty(x, y);
    this.pulseLostLife();
    useGameStore.getState().setStatus(`Vidas restantes: ${this.getRemainingLives()}/3`);
    this.updateGameplayHud();

    if (this.hairTouches >= gameSettings.gameplay.maxHairTouches) {
      this.time.delayedCall(220, () => this.endRun());
    }
  }

  private recordFleaKill() {
    const frenzySettings = gameSettings.gameplay.frenzy;
    const cutoff = this.elapsedMs - frenzySettings.windowMs;

    this.fleaKillTimestamps.push(this.elapsedMs);

    while (this.fleaKillTimestamps.length > 0 && this.fleaKillTimestamps[0] < cutoff) {
      this.fleaKillTimestamps.shift();
    }

    const killsInWindow = this.fleaKillTimestamps.length;

    if (killsInWindow > frenzySettings.baseKills) {
      this.triggerFrenzy(killsInWindow);
    }

    return killsInWindow;
  }

  private triggerFrenzy(killsInWindow: number) {
    const frenzySettings = gameSettings.gameplay.frenzy;
    const extraKills = Math.max(0, killsInWindow - frenzySettings.baseKills);
    const duration = Phaser.Math.Clamp(
      frenzySettings.baseDurationMs + extraKills * frenzySettings.extraDurationPerKillMs,
      frenzySettings.baseDurationMs,
      frenzySettings.maxDurationMs,
    );
    const wasActive = this.isFrenzyActive();
    const previousUntilMs = this.frenzyUntilMs;

    this.frenzyUntilMs = Math.max(this.frenzyUntilMs, this.elapsedMs + duration);
    this.frenzyLevel = Phaser.Math.Clamp(
      1 + Math.floor(extraKills / frenzySettings.killsPerLevel),
      1,
      frenzySettings.maxLevel,
    );

    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);

    if (wasActive) {
      const extensionMs = Math.max(0, this.frenzyUntilMs - previousUntilMs);

      if (extensionMs > 0) {
        this.soundSystem.playFrenzyExtend();
        this.pulseFrenzyExtension(killsInWindow, extensionMs);
      }

      return;
    }

    this.soundSystem.playFrenzy();
    this.startFrenzyFx(killsInWindow);
  }

  private isFrenzyActive() {
    return this.screen === 'playing' && this.elapsedMs < this.frenzyUntilMs;
  }

  private getRemainingLives() {
    return Math.max(0, gameSettings.gameplay.maxHairTouches - this.hairTouches);
  }

  private cancelFrenzyOnPenalty() {
    if (!this.isFrenzyActive() && !this.frenzyText) {
      return;
    }

    this.frenzyUntilMs = this.elapsedMs;
    this.stopFrenzyFx();
  }

  private maybeSpawnTickBoss() {
    const remainingMs = this.runDurationMs - this.elapsedMs;

    if (
      this.selectedPet !== 'dog' ||
      this.tickBossSpawned
    ) {
      return;
    }

    if (
      !this.tickBossWarningStarted &&
      remainingMs <= gameSettings.gameplay.tickBoss.appearsWithRemainingMs + 1_000
    ) {
      this.tickBossWarningStarted = true;
      this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
      void this.soundSystem.playTickBossWarning();
    }

    if (remainingMs > gameSettings.gameplay.tickBoss.appearsWithRemainingMs) {
      return;
    }

    this.tickBossSpawned = true;
    this.spawnTickBoss();
  }

  private spawnTickBoss() {
    const container = this.add.container(WIDTH + 180, 380).setDepth(7).setScale(0.08).setAlpha(0.4);
    const legs = this.createTickLegs();
    const body = this.add.image(0, 0, OTAMA_ASSETS.tick).setScale(0.42);
    const eyes = [
      this.add.circle(-24, -83, 13, 0xffffff, 0.95).setStrokeStyle(3, 0x4f110b, 1),
      this.add.circle(24, -83, 13, 0xffffff, 0.95).setStrokeStyle(3, 0x4f110b, 1),
    ];
    const pupils = [
      this.add.circle(-24, -83, 5, 0x111111, 1),
      this.add.circle(24, -83, 5, 0x111111, 1),
    ];
    const hitZone = this.add
      .rectangle(container.x, container.y, 310, 290, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(8);

    container.add([...legs, body, ...eyes, ...pupils]);

    this.tickBoss = {
      container,
      body,
      hitZone,
      legs,
      eyes,
      pupils,
      health: gameSettings.gameplay.tickBoss.health,
      vx: -380,
      vy: 210,
      baseY: container.y,
      nextDodgeAtMs: this.elapsedMs + 780,
      trailAtMs: this.elapsedMs,
    };

    hitZone.on(
      Phaser.Input.Events.POINTER_DOWN,
      (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();

        if (this.screen !== 'playing' || this.time.now < this.acceptGameplayInputAtMs) {
          return;
        }

        const point = this.getPointerWorldPoint(pointer);
        this.hitTickBoss(point.x, point.y);
      },
    );

    this.tweens.add({
      targets: container,
      alpha: 1,
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 420,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!container.active) {
          return;
        }

        this.tweens.add({
          targets: container,
          scaleX: 1.08,
          scaleY: 0.94,
          duration: 360,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });
    this.tweens.add({
      targets: eyes,
      scaleX: 1.18,
      scaleY: 0.84,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.showCenterNotice('¡Garrapata gigante!', '#ffdf56');
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.startTickBattleMusic();
    this.soundSystem.playTickAppear();
  }

  private createTickLegs() {
    const legs: Phaser.GameObjects.Rectangle[] = [];
    const legRows = [
      { y: -82, angle: 0.55, length: 82 },
      { y: -38, angle: 0.2, length: 96 },
      { y: 16, angle: -0.1, length: 106 },
      { y: 74, angle: -0.42, length: 94 },
    ];

    legRows.forEach((row, rowIndex) => {
      [-1, 1].forEach((side) => {
        const leg = this.add
          .rectangle(side * 62, row.y, row.length, 12, rowIndex % 2 === 0 ? 0x0b2b55 : 0x5d0806, 0.92)
          .setOrigin(side < 0 ? 1 : 0, 0.5)
          .setRotation(side * row.angle);

        legs.push(leg);
        this.tweens.add({
          targets: leg,
          rotation: side * (row.angle + 0.34),
          duration: 160 + rowIndex * 38,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      });
    });

    return legs;
  }

  private moveTickBoss(delta: number) {
    const boss = this.tickBoss;

    if (!boss) {
      return;
    }

    if (this.elapsedMs >= boss.nextDodgeAtMs) {
      const horizontalDirection = boss.container.x < WIDTH / 2 ? 1 : -1;

      boss.vx = horizontalDirection * Phaser.Math.Between(390, 680);
      boss.vy = Phaser.Math.Between(-360, 360);
      boss.nextDodgeAtMs = this.elapsedMs + Phaser.Math.Between(620, 1040);
      boss.container.setScale(1.16, 0.88);

      this.tweens.add({
        targets: boss.container,
        scaleX: 1,
        scaleY: 1,
        duration: 180,
        ease: 'Back.easeOut',
      });
    }

    boss.container.x += (boss.vx * delta) / 1000;
    boss.baseY += (boss.vy * delta) / 1000;
    boss.container.y =
      boss.baseY + Math.sin(this.elapsedMs * 0.011) * 92 + Math.cos(this.elapsedMs * 0.019) * 42;
    boss.container.rotation = Math.sin(this.elapsedMs * 0.009) * 0.18;
    boss.hitZone.setPosition(boss.container.x, boss.container.y);

    boss.pupils.forEach((pupil, index) => {
      pupil.x = (index === 0 ? -24 : 24) + Math.sin(this.elapsedMs * 0.018) * 5;
      pupil.y = -83 + Math.cos(this.elapsedMs * 0.014) * 4;
    });

    if (boss.container.x < 220) {
      boss.container.x = 220;
      boss.vx = Math.abs(boss.vx) * 1.14;
    }

    if (boss.container.x > WIDTH - 220) {
      boss.container.x = WIDTH - 220;
      boss.vx = -Math.abs(boss.vx) * 1.14;
    }

    if (boss.baseY < 250) {
      boss.baseY = 250;
      boss.vy = Math.abs(boss.vy);
    }

    if (boss.baseY > HEIGHT - 230) {
      boss.baseY = HEIGHT - 230;
      boss.vy = -Math.abs(boss.vy);
    }

    if (this.elapsedMs - boss.trailAtMs > 110) {
      boss.trailAtMs = this.elapsedMs;
      this.createTickTrail(boss.container.x, boss.container.y, boss.container.rotation);
    }
  }

  private createTickTrail(x: number, y: number, rotation: number) {
    const trail = this.add.image(x, y, OTAMA_ASSETS.tick).setScale(0.34).setAlpha(0.16).setRotation(rotation).setDepth(2);

    trail.setTint(0xff6f3c);
    this.tweens.add({
      targets: trail,
      alpha: 0,
      scaleX: 0.44,
      scaleY: 0.44,
      duration: 260,
      ease: 'Cubic.easeOut',
      onComplete: () => trail.destroy(),
    });
  }

  private hitTickBoss(x: number, y: number) {
    const boss = this.tickBoss;

    if (!boss || !boss.container.active) {
      return;
    }

    boss.health -= 1;
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playTickHit();
    this.particleFx.burst(x, y, 0xf05d4a, 18);
    this.cameras.main.shake(120, 0.004);

    if (boss.health > 0) {
      boss.body.setTint(0xff6f3c);
      boss.vx *= 1.34;
      boss.vy = Phaser.Math.Between(-430, 430);
      boss.nextDodgeAtMs = this.elapsedMs + 360;
      this.showFloatingLabel(x, y - 30, '¡Debilitada!\n1 toque más', '#ffdf56');
      this.tweens.add({
        targets: boss.container,
        scaleX: 0.9,
        scaleY: 1.16,
        duration: 110,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeOut',
      });
      return;
    }

    this.burstTickBoss(x, y);
  }

  private burstTickBoss(x: number, y: number) {
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playTickBurst();
    this.destroyTickBoss();
    this.cameras.main.flash(260, 255, 120, 60, false);
    this.cameras.main.shake(260, 0.008);
    this.particleFx.burst(x, y, 0xff4d2e, 42);
    this.createGooBurst(x, y);
    this.spawnMiniTicks(x, y);
    this.spawnTickBonuses(x, y);
    this.showCenterNotice('¡Garrapata reventada!', '#ffdf56');
  }

  private createGooBurst(x: number, y: number) {
    const colors = [0x8a120c, 0xff6f3c, 0xffdf56, 0x4f110b];

    for (let index = 0; index < 24; index += 1) {
      const splat = this.add
        .ellipse(x, y, Phaser.Math.Between(18, 52), Phaser.Math.Between(10, 26), Phaser.Math.RND.pick(colors), 0.72)
        .setDepth(9)
        .setAngle(Phaser.Math.Between(0, 180));
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const distance = Phaser.Math.Between(80, 260);

      this.tweens.add({
        targets: splat,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: Phaser.Math.Between(520, 900),
        ease: 'Cubic.easeOut',
        onComplete: () => splat.destroy(),
      });
    }
  }

  private spawnMiniTicks(x: number, y: number) {
    for (let index = 0; index < gameSettings.gameplay.tickBoss.miniTicks; index += 1) {
      const sprite = this.add
        .image(x, y, OTAMA_ASSETS.tick)
        .setScale(Phaser.Math.FloatBetween(0.07, 0.09))
        .setAngle(Phaser.Math.Between(-35, 35))
        .setDepth(5)
        .setTint(0xff8f3c);
      const hitSize = Math.max(150, sprite.displayWidth * 2.6, sprite.displayHeight * 2.6);
      const hitZone = this.add
        .rectangle(x, y, hitSize, hitSize, 0xffffff, 0.001)
        .setInteractive({ useHandCursor: true })
        .setDepth(6);
      const miniTick: MiniTickTarget = {
        sprite,
        hitZone,
        vx: Phaser.Math.Between(-250, 250) || 180,
        vy: Phaser.Math.Between(-185, 185) || -140,
        spawnedAtMs: this.elapsedMs,
      };

      hitZone.on(
        Phaser.Input.Events.POINTER_DOWN,
        (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
          event.stopPropagation();

          if (this.screen !== 'playing') {
            return;
          }

          const point = this.getPointerWorldPoint(pointer);
          this.collectMiniTick(miniTick, point.x, point.y);
        },
      );

      this.miniTicks.push(miniTick);
    }
  }

  private moveMiniTicks(delta: number) {
    for (let index = this.miniTicks.length - 1; index >= 0; index -= 1) {
      const miniTick = this.miniTicks[index];
      const wobble = Math.sin((this.elapsedMs + index * 120) * 0.012) * 65;

      miniTick.sprite.x += ((miniTick.vx + wobble) * delta) / 1000;
      miniTick.sprite.y += (miniTick.vy * delta) / 1000;
      miniTick.sprite.angle += (miniTick.vx > 0 ? 1 : -1) * delta * 0.18;
      miniTick.hitZone.setPosition(miniTick.sprite.x, miniTick.sprite.y);

      if (miniTick.sprite.x < 80 || miniTick.sprite.x > WIDTH - 80) {
        miniTick.vx *= -1;
      }

      if (miniTick.sprite.y < 150 || miniTick.sprite.y > HEIGHT - 80) {
        miniTick.vy *= -1;
      }

      if (this.elapsedMs - miniTick.spawnedAtMs > gameSettings.gameplay.tickBoss.miniTickLifetimeMs) {
        this.destroyMiniTick(miniTick);
        this.miniTicks.splice(index, 1);
      }
    }
  }

  private collectMiniTick(miniTick: MiniTickTarget, x: number, y: number) {
    if (!miniTick.sprite.active || !this.miniTicks.includes(miniTick)) {
      return;
    }

    const scoreGain = gameSettings.gameplay.tickBoss.miniTickScore;
    this.score += scoreGain;
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.soundSystem.playReward();
    this.particleFx.burst(x, y, 0xff8f00, 16);
    this.showFloatingScore(x, y, scoreGain, false, 0);

    const index = this.miniTicks.indexOf(miniTick);

    if (index >= 0) {
      this.miniTicks.splice(index, 1);
    }

    this.tweens.killTweensOf(miniTick.sprite);
    miniTick.hitZone.destroy();
    this.tweens.add({
      targets: miniTick.sprite,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 130,
      ease: 'Back.easeIn',
      onComplete: () => miniTick.sprite.destroy(),
    });
    this.updateGameplayHud();
  }

  private destroyMiniTick(miniTick: MiniTickTarget) {
    this.tweens.killTweensOf(miniTick.sprite);
    miniTick.hitZone.destroy();
    miniTick.sprite.destroy();
  }

  private spawnTickBonuses(x: number, y: number) {
    const bonuses: Array<{ kind: BonusKind; value: number; offsetX: number; offsetY: number }> = [
      ...gameSettings.gameplay.tickBoss.timeBonusesSeconds.map((seconds, index) => ({
        kind: 'time' as const,
        value: seconds,
        offsetX: index === 0 ? -210 : 210,
        offsetY: -125,
      })),
      ...Array.from({ length: gameSettings.gameplay.tickBoss.lifeBonuses }, (_, index) => ({
        kind: 'life' as const,
        value: 1,
        offsetX: index === 0 ? -130 : 130,
        offsetY: 115,
      })),
    ];

    bonuses.forEach((bonus, index) => {
      const targetX = Phaser.Math.Clamp(x + bonus.offsetX, 120, WIDTH - 120);
      const targetY = Phaser.Math.Clamp(y + bonus.offsetY, 180, HEIGHT - 120);
      this.createBonusCollectible(bonus.kind, bonus.value, x, y, targetX, targetY, index);
    });
  }

  private createBonusCollectible(
    kind: BonusKind,
    value: number,
    startX: number,
    startY: number,
    targetX: number,
    targetY: number,
    index: number,
  ) {
    const container = this.add.container(startX, startY).setDepth(10);
    const fill = kind === 'time' ? 0x1f7ae0 : 0xf05d4a;
    const stroke = kind === 'time' ? 0xffdf56 : 0xffffff;
    const circle = this.add.circle(0, 0, 54, fill, 0.95).setStrokeStyle(7, stroke, 1);
    const mainLabel = kind === 'time' ? `+${value}s` : '\u2665';
    const label = this.addText(0, kind === 'time' ? -2 : -7, mainLabel, kind === 'time' ? 34 : 62, '#ffffff')
      .setOrigin(0.5)
      .setStroke(kind === 'time' ? '#102c5e' : '#8a120c', 5);
    const subLabel = kind === 'time' ? 'TIEMPO' : 'VIDA';
    const sub = this.addText(0, 48, subLabel, 18, '#ffffff').setOrigin(0.5).setAlpha(0.9);
    const hitZone = this.add
      .rectangle(startX, startY, 140, 140, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(11);
    const collectible: BonusCollectible = {
      container,
      hitZone,
      kind,
      value,
      floatSpeed: kind === 'time' ? 74 : 58,
      drift: kind === 'time' ? 24 : 18,
      phase: Phaser.Math.FloatBetween(0, Math.PI * 2),
      activeAtMs: this.elapsedMs + 420 + index * 80,
    };

    container.add([circle, label, sub]);
    hitZone.on(
      Phaser.Input.Events.POINTER_DOWN,
      (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();

        if (this.screen !== 'playing') {
          return;
        }

        this.collectBonus(collectible);
      },
    );

    this.bonusCollectibles.push(collectible);
    this.tweens.add({
      targets: [container, hitZone],
      x: targetX,
      y: targetY,
      duration: 420 + index * 80,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: container,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 430,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private moveBonusCollectibles(delta: number) {
    for (let index = this.bonusCollectibles.length - 1; index >= 0; index -= 1) {
      const collectible = this.bonusCollectibles[index];

      if (this.elapsedMs >= collectible.activeAtMs) {
        collectible.container.y -= (collectible.floatSpeed * delta) / 1000;
        collectible.container.x += Math.sin(this.elapsedMs * 0.006 + collectible.phase) * collectible.drift * (delta / 1000);

        if (collectible.container.y < 150) {
          collectible.container.y = 150;
          collectible.floatSpeed *= -0.42;
        }

        if (collectible.container.y > HEIGHT - 110) {
          collectible.container.y = HEIGHT - 110;
          collectible.floatSpeed = -Math.abs(collectible.floatSpeed);
        }
      }

      collectible.hitZone.setPosition(collectible.container.x, collectible.container.y);
    }
  }

  private collectBonus(collectible: BonusCollectible) {
    if (!collectible.container.active || !this.bonusCollectibles.includes(collectible)) {
      return;
    }

    const x = collectible.container.x;
    const y = collectible.container.y;
    const index = this.bonusCollectibles.indexOf(collectible);

    if (index >= 0) {
      this.bonusCollectibles.splice(index, 1);
    }

    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    this.playBonusSound(collectible.kind);

    if (collectible.kind === 'time') {
      this.runDurationMs += collectible.value * 1000;
      this.showFloatingLabel(x, y, `+${collectible.value}s`, '#ffdf56');
    } else {
      const hadMissingLife = this.hairTouches > 0;

      this.hairTouches = Math.max(0, this.hairTouches - collectible.value);
      this.showFloatingLabel(x, y, hadMissingLife ? '+VIDA' : 'VIDA LLENA', '#ffdf56');
      this.updateLivesHud();
    }

    this.playBonusCollectFx(collectible.kind, x, y, collectible.value);
    this.tweens.killTweensOf(collectible.container);
    this.tweens.killTweensOf(collectible.hitZone);
    collectible.hitZone.destroy();
    this.tweens.add({
      targets: collectible.container,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 160,
      ease: 'Back.easeIn',
      onComplete: () => collectible.container.destroy(true),
    });
    this.updateGameplayHud();
  }

  private playBonusSound(kind: BonusKind) {
    if (kind === 'time') {
      this.soundSystem.playTimeBonus();
      return;
    }

    this.soundSystem.playLifeBonus();
  }

  private playBonusCollectFx(kind: BonusKind, x: number, y: number, value: number) {
    const isTime = kind === 'time';
    const primary = isTime ? 0x1f7ae0 : 0xf05d4a;
    const secondary = isTime ? 0xffdf56 : 0xffffff;
    const flash = isTime ? { r: 35, g: 150, b: 255 } : { r: 255, g: 80, b: 95 };
    const headline = isTime ? `+${value}s TIEMPO` : 'CORAZÓN DE VIDA';

    this.cameras.main.flash(180, flash.r, flash.g, flash.b, false);
    this.particleFx.burst(x, y, primary, 32);
    this.particleFx.burst(x, y, secondary, 22);

    for (let index = 0; index < 4; index += 1) {
      const ring = this.add
        .circle(x, y, 42 + index * 18, primary, 0)
        .setStrokeStyle(8 - index, index % 2 === 0 ? primary : secondary, 0.9)
        .setDepth(15)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.tweens.add({
        targets: ring,
        scaleX: 2.4 + index * 0.4,
        scaleY: 2.4 + index * 0.4,
        alpha: 0,
        duration: 520 + index * 90,
        ease: 'Cubic.easeOut',
        onComplete: () => ring.destroy(),
      });
    }

    for (let index = 0; index < 10; index += 1) {
      const beam = this.add
        .rectangle(x, y, 16, Phaser.Math.Between(120, 230), index % 2 === 0 ? primary : secondary, 0.58)
        .setOrigin(0.5, 1)
        .setRotation((Math.PI * 2 * index) / 10)
        .setDepth(15)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.tweens.add({
        targets: beam,
        alpha: 0,
        scaleY: 1.9,
        duration: 420,
        ease: 'Cubic.easeOut',
        onComplete: () => beam.destroy(),
      });
    }

    const text = this.addText(x, y - 96, headline, isTime ? 42 : 38, '#ffffff')
      .setOrigin(0.5)
      .setStroke(isTime ? '#102c5e' : '#8a120c', 9)
      .setDepth(16);

    this.tweens.add({
      targets: text,
      y: y - 165,
      alpha: 0,
      scaleX: 1.26,
      scaleY: 1.26,
      duration: 760,
      ease: 'Back.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  private destroyBonusCollectible(collectible: BonusCollectible) {
    this.tweens.killTweensOf(collectible.container);
    this.tweens.killTweensOf(collectible.hitZone);
    collectible.hitZone.destroy();
    collectible.container.destroy(true);
  }

  private destroyTickBoss() {
    const boss = this.tickBoss;

    if (!boss) {
      return;
    }

    this.tweens.killTweensOf(boss.container);
    this.tweens.killTweensOf(boss.legs);
    this.tweens.killTweensOf(boss.eyes);
    boss.hitZone.destroy();
    boss.container.destroy(true);
    this.tickBoss = undefined;
    this.soundSystem.stopTickBattleMusic();
  }

  private spawnFlea() {
    const progress = Phaser.Math.Clamp(this.elapsedMs / (gameSettings.gameplay.durationSeconds * 1000), 0, 1);
    const speed = Phaser.Math.Linear(gameSettings.gameplay.initialFleaSpeed, gameSettings.gameplay.finalFleaSpeed, progress);
    const asset =
      this.selectedPet === 'dog'
        ? Phaser.Math.RND.pick([OTAMA_ASSETS.dogFlea1, OTAMA_ASSETS.dogFlea2])
        : Phaser.Math.RND.pick([OTAMA_ASSETS.catFlea1, OTAMA_ASSETS.catFlea2]);
    const flea = this.add
      .image(Phaser.Math.Between(150, WIDTH - 150), -84, asset)
      .setScale(0.18)
      .setAngle(Phaser.Math.Between(-16, 16))
      .setDepth(3);
    const hitSize = Math.max(gameSettings.kiosk.minTouchTarget, flea.displayWidth * 1.75, flea.displayHeight * 1.75);
    const hitZone = this.add
      .rectangle(flea.x, flea.y, hitSize, hitSize, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(4);
    const target: FleaTarget = {
      sprite: flea,
      hitZone,
      speed: (speed + Phaser.Math.Between(-25, 35)) * (this.isFrenzyActive() ? 1.16 : 1),
      drift: Phaser.Math.FloatBetween(-0.0014, 0.0014),
    };

    hitZone.on(
      Phaser.Input.Events.POINTER_DOWN,
      (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();

        if (this.screen !== 'playing' || this.time.now < this.acceptGameplayInputAtMs) {
          return;
        }

        const point = this.getPointerWorldPoint(pointer);

        if (point.y < GAMEPLAY_INPUT_TOP_SAFE_Y) {
          return;
        }

        this.lastFleaHitPointerId = pointer.id;
        this.lastFleaHitAtMs = this.time.now;
        this.collectFlea(target, point.x, point.y);
      },
    );

    this.fleas.push(target);
    this.tweens.add({
      targets: flea,
      scaleX: 0.2,
      scaleY: 0.2,
      duration: 260,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private moveFleas(delta: number) {
    for (let index = this.fleas.length - 1; index >= 0; index -= 1) {
      const flea = this.fleas[index];
      flea.sprite.y += (flea.speed * delta) / 1000;
      flea.sprite.x += Math.sin(this.elapsedMs * flea.drift) * 1.8;
      flea.sprite.angle += flea.drift * delta * 16;
      flea.hitZone.setPosition(flea.sprite.x, flea.sprite.y);

      if (flea.sprite.y > HEIGHT + 110) {
        this.destroyFlea(flea);
        this.fleas.splice(index, 1);
      }
    }
  }

  private scheduleNextFlea() {
    if (this.screen !== 'playing') {
      return;
    }

    const progress = Phaser.Math.Clamp(this.elapsedMs / (gameSettings.gameplay.durationSeconds * 1000), 0, 1);
    const delay =
      Phaser.Math.Linear(gameSettings.gameplay.fleaSpawnStartMs, gameSettings.gameplay.fleaSpawnEndMs, progress) *
      (this.isFrenzyActive() ? gameSettings.gameplay.frenzy.spawnDelayMultiplier : 1);

    this.spawnTimer = this.time.delayedCall(delay, () => {
      this.spawnFlea();
      this.scheduleNextFlea();
    });
  }

  private scheduleHairCue() {
    if (this.screen !== 'playing') {
      return;
    }

    this.hairCueTimer = this.time.delayedCall(Phaser.Math.Between(850, 1450), () => {
      this.createHairCue();
      this.scheduleHairCue();
    });
  }

  private createHairCue() {
    const graphics = this.add.graphics();
    const x = Phaser.Math.Between(170, WIDTH - 170);
    const y = Phaser.Math.Between(170, HEIGHT - 150);
    const width = Phaser.Math.Between(120, 260);
    const color = this.selectedPet === 'dog' ? 0xffef8f : 0x5b5a37;

    graphics.lineStyle(8, color, 0.5);

    for (let i = 0; i < 5; i += 1) {
      const offsetY = i * 14;
      let lastX = x - width / 2;
      let lastY = y + offsetY;

      for (let segment = 1; segment <= 12; segment += 1) {
        const t = segment / 12;
        const nextX = x - width / 2 + width * t;
        const nextY = y + offsetY - Math.sin(t * Math.PI) * 30;
        graphics.lineBetween(lastX, lastY, nextX, nextY);
        lastX = nextX;
        lastY = nextY;
      }
    }

    this.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: 900,
      delay: 420,
      onComplete: () => graphics.destroy(),
    });
  }

  private removeFlea(flea: FleaTarget) {
    const index = this.fleas.indexOf(flea);

    if (index >= 0) {
      this.fleas.splice(index, 1);
    }

    this.tweens.killTweensOf(flea.sprite);
    flea.hitZone.disableInteractive();
    flea.hitZone.destroy();
    this.tweens.add({
      targets: flea.sprite,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 120,
      ease: 'Back.easeIn',
      onComplete: () => flea.sprite.destroy(),
    });
  }

  private destroyFlea(flea: FleaTarget) {
    this.tweens.killTweensOf(flea.sprite);
    flea.hitZone.destroy();
    flea.sprite.destroy();
  }

  private flashPenalty(x: number, y: number) {
    const marker = this.add.circle(x, y, 74, 0xf05d4a, 0.22).setStrokeStyle(8, 0xf05d4a, 0.9);

    this.tweens.add({
      targets: marker,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 260,
      ease: 'Cubic.easeOut',
      onComplete: () => marker.destroy(),
    });

    this.cameras.main.shake(90, 0.003);
  }

  private addGameplayHud() {
    const panel = this.add.rectangle(WIDTH / 2, 64, WIDTH - 96, 94, 0x234b25, 0.86).setStrokeStyle(4, 0xffdf56, 0.7);

    this.scoreText = this.addText(210, 64, 'Puntos: 0', 42, '#ffffff').setOrigin(0, 0.5);
    this.timerText = this.addText(WIDTH / 2, 64, '30s', 58, '#ffdf56').setOrigin(0.5);
    this.livesText = this.addText(WIDTH - 430, 64, 'Vidas', 42, '#ffffff').setOrigin(1, 0.5);
    panel.setDepth(10);
    this.scoreText.setDepth(11);
    this.timerText.setDepth(11);
    this.livesText.setDepth(11);

    for (let index = 0; index < gameSettings.gameplay.maxHairTouches; index += 1) {
      const icon = this.add
        .circle(WIDTH - 380 + index * 58, 64, 20, 0xffdf56, 1)
        .setStrokeStyle(5, 0xffffff, 0.95)
        .setDepth(11);

      this.lifeIcons.push(icon);
    }

    this.updateLivesHud();
  }

  private updateGameplayHud() {
    const remaining = Math.max(0, Math.ceil((this.runDurationMs - this.elapsedMs) / 1000));
    this.scoreText?.setText(`Puntos: ${this.score}`);
    this.timerText?.setText(`${remaining}s`);
    this.errorsText?.setText(`Fallos: ${this.hairTouches}/3`);
    this.updateLivesHud();
    this.publishRuntimeState();
  }

  private syncGameplayAudio() {
    const audioEnabled = useGameStore.getState().audioEnabled;

    this.soundSystem.setEnabled(audioEnabled);

    if (!audioEnabled || this.screen !== 'playing') {
      this.soundSystem.stopAllLoops();
      return;
    }

    const remainingMs = this.runDurationMs - this.elapsedMs;

    if (this.tickBoss || this.soundSystem.isTickBattleMusicActive()) {
      this.soundSystem.stopGameplayMusic();
      this.soundSystem.stopFinalSuspense();
      return;
    }

    this.soundSystem.startGameplayMusic();

    if (remainingMs > 0 && remainingMs <= 7_000) {
      this.soundSystem.startFinalSuspense();
      return;
    }

    this.soundSystem.stopFinalSuspense();
  }

  private updateLivesHud() {
    const remainingLives = this.getRemainingLives();

    this.lifeIcons.forEach((icon, index) => {
      const isAlive = index < remainingLives;

      icon.setFillStyle(isAlive ? 0xffdf56 : 0x62231d, isAlive ? 1 : 0.92);
      icon.setStrokeStyle(5, isAlive ? 0xffffff : 0xf05d4a, isAlive ? 0.95 : 0.9);
      icon.setScale(isAlive ? 1 : 0.78);
      icon.setAlpha(isAlive ? 1 : 0.72);
    });
  }

  private pulseLostLife() {
    const lostIndex = gameSettings.gameplay.maxHairTouches - this.hairTouches;
    const icon = this.lifeIcons[lostIndex];

    if (!icon) {
      return;
    }

    this.tweens.add({
      targets: icon,
      scaleX: 1.45,
      scaleY: 1.45,
      alpha: 0.25,
      duration: 140,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeOut',
    });
  }

  private startFrenzyFx(killsInWindow: number) {
    this.frenzyOverlay?.destroy();
    this.frenzyText?.destroy();

    this.frenzyOverlay = this.add
      .rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0xffdf56, 0.13)
      .setDepth(6)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.frenzyText = this.addText(WIDTH / 2, 185, '', 62, '#ffffff')
      .setOrigin(0.5)
      .setAlign('center')
      .setStroke('#f05d4a', 12)
      .setDepth(13);

    this.updateFrenzyText(killsInWindow);
    this.createFrenzyConfetti(44 + this.frenzyLevel * 8);
    this.cameras.main.flash(220, 255, 223, 86, false);
    this.cameras.main.shake(220, 0.006);

    this.tweens.add({
      targets: this.frenzyOverlay,
      alpha: 0.24,
      duration: 280,
      yoyo: true,
      repeat: 5,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: this.frenzyText,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 210,
      yoyo: true,
      repeat: 1,
      ease: 'Back.easeOut',
    });
  }

  private pulseFrenzyExtension(killsInWindow: number, extensionMs: number) {
    this.updateFrenzyText(killsInWindow);
    this.createFrenzyConfetti(12 + this.frenzyLevel * 4);

    const bonusSeconds = Math.max(1, Math.ceil(extensionMs / 1000));
    const bonus = this.addText(WIDTH / 2, 285, `+${bonusSeconds}s de fiebre`, 44, '#ffdf56')
      .setOrigin(0.5)
      .setStroke('#21411f', 8)
      .setDepth(14);

    this.tweens.add({
      targets: bonus,
      y: 235,
      alpha: 0,
      scaleX: 1.18,
      scaleY: 1.18,
      duration: 620,
      ease: 'Cubic.easeOut',
      onComplete: () => bonus.destroy(),
    });
  }

  private updateFrenzyState() {
    if (this.frenzyText && this.isFrenzyActive()) {
      this.updateFrenzyText(this.fleaKillTimestamps.length);
      return;
    }

    if (this.frenzyText && !this.isFrenzyActive()) {
      this.stopFrenzyFx();
    }
  }

  private updateFrenzyText(killsInWindow: number) {
    const remainingMs = Math.max(0, this.frenzyUntilMs - this.elapsedMs);
    const remainingSeconds = Math.ceil(remainingMs / 1000);

    this.frenzyText?.setText(
      `FIEBRE ANTIPULGAS x${gameSettings.gameplay.frenzy.scoreMultiplier}\n${remainingSeconds}s | racha ${killsInWindow}`,
    );
  }

  private stopFrenzyFx() {
    const overlay = this.frenzyOverlay;
    const text = this.frenzyText;

    this.frenzyOverlay = undefined;
    this.frenzyText = undefined;
    this.frenzyLevel = 0;

    if (overlay?.active) {
      this.tweens.killTweensOf(overlay);
      this.tweens.add({
        targets: overlay,
        alpha: 0,
        duration: 220,
        onComplete: () => overlay.destroy(),
      });
    }

    if (text?.active) {
      this.tweens.killTweensOf(text);
      this.tweens.add({
        targets: text,
        alpha: 0,
        scaleX: 0.9,
        scaleY: 0.9,
        duration: 220,
        onComplete: () => text.destroy(),
      });
    }
  }

  private createFrenzyConfetti(count: number) {
    const colors = [0xffdf56, 0xff8f00, 0xffffff, 0x7bd83e];

    for (let index = 0; index < count; index += 1) {
      const x = Phaser.Math.Between(80, WIDTH - 80);
      const y = Phaser.Math.Between(140, HEIGHT - 80);
      const particle = this.add
        .circle(x, y, Phaser.Math.Between(5, 12), Phaser.Math.RND.pick(colors), 0.92)
        .setDepth(12)
        .setBlendMode(Phaser.BlendModes.ADD);
      const angle = Phaser.Math.FloatBetween(-Math.PI, Math.PI);
      const distance = Phaser.Math.Between(80, 260);

      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: Phaser.Math.Between(560, 940),
        ease: 'Cubic.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  }

  private showFloatingScore(x: number, y: number, scoreGain: number, isFrenzy: boolean, killsInWindow: number) {
    const text = this.addText(
      x,
      y - 42,
      isFrenzy ? `+${scoreGain} x${gameSettings.gameplay.frenzy.scoreMultiplier}` : `+${scoreGain}`,
      isFrenzy ? 42 : 34,
      isFrenzy ? '#ffdf56' : '#ffffff',
    )
      .setOrigin(0.5)
      .setStroke(isFrenzy ? '#f05d4a' : '#21411f', isFrenzy ? 8 : 6)
      .setDepth(14);

    this.tweens.add({
      targets: text,
      y: y - 112,
      alpha: 0,
      scaleX: isFrenzy ? 1.26 : 1.08,
      scaleY: isFrenzy ? 1.26 : 1.08,
      duration: isFrenzy ? 620 : 430,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });

    if (!isFrenzy && killsInWindow >= gameSettings.gameplay.frenzy.baseKills - 1) {
      const remaining = gameSettings.gameplay.frenzy.baseKills + 1 - killsInWindow;
      const label = remaining > 0 ? `${remaining} para fiebre` : 'Fiebre lista';
      const hint = this.addText(x, y + 26, label, 25, '#ffdf56')
        .setOrigin(0.5)
        .setStroke('#21411f', 5)
        .setDepth(14);

      this.tweens.add({
        targets: hint,
        y: y - 20,
        alpha: 0,
        duration: 480,
        ease: 'Cubic.easeOut',
        onComplete: () => hint.destroy(),
      });
    }
  }

  private showFloatingLabel(x: number, y: number, label: string, color: string) {
    const text = this.addText(x, y, label, 34, color)
      .setOrigin(0.5)
      .setAlign('center')
      .setStroke('#21411f', 7)
      .setDepth(14);

    this.tweens.add({
      targets: text,
      y: y - 82,
      alpha: 0,
      scaleX: 1.18,
      scaleY: 1.18,
      duration: 680,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  private showCenterNotice(label: string, color: string) {
    const text = this.addText(WIDTH / 2, 190, label, 58, color)
      .setOrigin(0.5)
      .setStroke('#21411f', 10)
      .setDepth(14);

    this.tweens.add({
      targets: text,
      y: 145,
      alpha: 0,
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 1250,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  private createReynaRig(
    x: number,
    y: number,
    scale: number,
    options: {
      messages?: string[];
      bubbleX?: number;
      bubbleY?: number;
      bubbleScale?: number;
    } = {},
  ) {
    const ownerScreen = this.screen;
    const root = this.add.container(x, y).setDepth(3).setScale(scale).setAlpha(0);
    const speechMessages = options.messages ?? REYNA_INTRO_MESSAGES;
    const speechBubbleBaseScale = (options.bubbleScale ?? 0.84) / scale;
    const shadow = this.add.ellipse(36, 332, 560, 72, 0x21411f, 0.17);
    const tail = this.add
      .image(-92, 294, OTAMA_ASSETS.reynaTail)
      .setOrigin(0.86, 0.72)
      .setScale(0.78)
      .setAngle(-6)
      .setCrop(0, 0, 340, 309);
    const sidePatch = this.add.ellipse(-10, 292, 276, 122, 0x2b1729, 1).setAngle(-6);
    const body = this.add.image(58, 242, OTAMA_ASSETS.reynaBody).setOrigin(0.5);
    const neckBridge = this.add.ellipse(36, 142, 210, 132, 0xfffbec, 0.98).setAngle(-4);
    const headGroup = this.add.container(0, -62);
    const leftEar = this.add.image(-162, -214, OTAMA_ASSETS.reynaEarLeft).setOrigin(0.72, 0.82).setAngle(-4);
    const rightEar = this.add.image(152, -214, OTAMA_ASSETS.reynaEarRight).setOrigin(0.28, 0.82).setAngle(4);
    const head = this.add.image(0, 0, OTAMA_ASSETS.reynaHead).setOrigin(0.5);
    const leftBrow = this.add.image(-150, -176, OTAMA_ASSETS.reynaBrowLeft).setOrigin(0.5).setAngle(-7).setScale(1.04);
    const rightBrow = this.add.image(60, -164, OTAMA_ASSETS.reynaBrowRight).setOrigin(0.5).setAngle(6).setScale(0.76);
    const leftEye = this.add.image(-154, -136, OTAMA_ASSETS.reynaEyeLeft).setOrigin(0.5).setScale(1.16).setAngle(3);
    const rightEye = this.add.image(58, -132, OTAMA_ASSETS.reynaEyeLeft).setOrigin(0.5).setScale(1.16).setAngle(4);
    rightEye.setFlipX(true);
    const leftPupil = this.add.image(-152, -132, OTAMA_ASSETS.reynaPupil).setOrigin(0.5).setScale(0.58, 0.54).setAngle(94);
    const rightPupil = this.add.image(60, -128, OTAMA_ASSETS.reynaPupil).setOrigin(0.5).setScale(0.58, 0.54).setAngle(94);
    const leftPupilGlint = this.add.ellipse(-148, -142, 7, 11, 0xffffff, 0.92).setAngle(10);
    const rightPupilGlint = this.add.ellipse(64, -138, 7, 11, 0xffffff, 0.92).setAngle(10);
    const leftEyeClosed = this.add
      .image(-152, -126, OTAMA_ASSETS.reynaEyeLeftClosed)
      .setOrigin(0.5)
      .setScale(1.42)
      .setAlpha(0);
    const rightEyeClosed = this.add
      .image(64, -132, OTAMA_ASSETS.reynaEyeRightClosed)
      .setOrigin(0.5)
      .setScale(1.04)
      .setAngle(1)
      .setAlpha(0);
    const noseMouth = this.add.image(-44, 32, OTAMA_ASSETS.reynaNoseMouth).setOrigin(0.5);
    const hitZone = this.add.rectangle(0, 12, 740, 760, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    const bubble = this.createReynaSpeechBubble(options.bubbleX ?? -390, options.bubbleY ?? -388, speechBubbleBaseScale);
    const rig: ReynaRig = {
      root,
      headGroup,
      tail,
      leftEar,
      rightEar,
      leftEye,
      rightEye,
      leftEyeClosed,
      rightEyeClosed,
      leftPupil,
      rightPupil,
      leftPupilGlint,
      rightPupilGlint,
      noseMouth,
      leftBrow,
      rightBrow,
      bubble: bubble.container,
      speechText: bubble.text,
      speechMessages,
      speechIndex: 0,
      speechBubbleBaseScale,
      hitZone,
      baseLeftPupil: new Phaser.Math.Vector2(leftPupil.x, leftPupil.y),
      baseRightPupil: new Phaser.Math.Vector2(rightPupil.x, rightPupil.y),
      baseLeftPupilGlint: new Phaser.Math.Vector2(leftPupilGlint.x, leftPupilGlint.y),
      baseRightPupilGlint: new Phaser.Math.Vector2(rightPupilGlint.x, rightPupilGlint.y),
    };

    headGroup.add([
      leftEar,
      rightEar,
      head,
      leftBrow,
      rightBrow,
      leftEye,
      rightEye,
      leftPupil,
      rightPupil,
      leftPupilGlint,
      rightPupilGlint,
      leftEyeClosed,
      rightEyeClosed,
      noseMouth,
    ]);
    root.add([shadow, tail, sidePatch, body, neckBridge, headGroup, hitZone, bubble.container]);

    hitZone.on(
      Phaser.Input.Events.POINTER_DOWN,
      (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();

        if (this.screen !== ownerScreen || !root.active) {
          return;
        }

        this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
        void this.soundSystem.unlock();
        this.soundSystem.playTap();
        this.animateReynaReaction(rig);
        this.showReynaSpeech(rig, Phaser.Math.Between(0, rig.speechMessages.length - 1), true);
      },
    );

    this.tweens.add({
      targets: root,
      alpha: 1,
      y: y - 10,
      duration: 520,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: root,
      y: y - 24,
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 520,
    });
    this.tweens.add({
      targets: headGroup,
      angle: 0.9,
      x: 0,
      y: -66,
      duration: 1420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: tail,
      angle: 4,
      x: tail.x - 14,
      y: tail.y - 6,
      duration: 1180,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftEar, rightEar],
      angle: '+=6',
      duration: 1280,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftBrow, rightBrow],
      y: '-=7',
      duration: 980,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: noseMouth,
      scaleX: 1.04,
      scaleY: 1.05,
      duration: 760,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftPupil, rightPupil],
      scaleY: '+=0.05',
      duration: 820,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.scheduleReynaBlink(rig);
    this.scheduleReynaEarTwitch(rig);
    this.scheduleReynaLook(rig);
    this.showReynaSpeech(rig, 0);
    this.scheduleReynaSpeech(rig);

    return rig;
  }

  private createReynaSpeechBubble(x: number, y: number, baseScale: number) {
    const container = this.add.container(x, y).setScale(baseScale * 0.86).setAlpha(0);
    const graphics = this.add.graphics();
    const glow = this.add.ellipse(0, 0, 390, 178, 0xff7bd5, 0.16).setBlendMode(Phaser.BlendModes.ADD);
    const text = this.addText(0, -4, '', 29, '#2b1729')
      .setOrigin(0.5)
      .setAlign('center')
      .setWordWrapWidth(300)
      .setLineSpacing(4);

    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillRoundedRect(-180, -82, 360, 164, 34);
    graphics.lineStyle(7, 0xffdf56, 1);
    graphics.strokeRoundedRect(-180, -82, 360, 164, 34);
    graphics.lineStyle(4, 0x9f1d78, 0.5);
    graphics.strokeRoundedRect(-166, -68, 332, 136, 28);
    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillTriangle(92, 62, 146, 106, 128, 48);
    graphics.lineStyle(6, 0xffdf56, 1);
    graphics.lineBetween(92, 62, 146, 106);
    graphics.lineBetween(146, 106, 128, 48);
    container.add([glow, graphics, text]);

    this.tweens.add({
      targets: glow,
      alpha: 0.32,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 980,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return { container, text };
  }

  private scheduleReynaSpeech(rig: ReynaRig) {
    this.time.delayedCall(Phaser.Math.Between(3200, 5600), () => {
      if (!rig.root.active) {
        return;
      }

      this.showReynaSpeech(rig, (rig.speechIndex + 1) % rig.speechMessages.length);
      this.scheduleReynaSpeech(rig);
    });
  }

  private showReynaSpeech(rig: ReynaRig, index: number, isReaction = false) {
    if (!rig.root.active) {
      return;
    }

    const message = rig.speechMessages[index] ?? rig.speechMessages[0] ?? '';

    rig.speechIndex = index;
    rig.speechText.setText(message);
    rig.bubble.setAlpha(0);
    rig.bubble.setScale(rig.speechBubbleBaseScale * 0.82);
    rig.bubble.setAngle(Phaser.Math.Between(-2, 2));

    this.tweens.add({
      targets: rig.bubble,
      alpha: 1,
      scaleX: rig.speechBubbleBaseScale,
      scaleY: rig.speechBubbleBaseScale,
      duration: isReaction ? 180 : 320,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: rig.speechText,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeOut',
      delay: 60,
    });

    if (isReaction) {
      this.createReynaSparkles(rig.root.x - 20, rig.root.y - 260);
    }
  }

  private createReynaSparkles(x: number, y: number) {
    const colors = [0xffdf56, 0xffffff, 0xff7bd5, 0x9f1d78];

    for (let index = 0; index < 12; index += 1) {
      const sparkle = this.add
        .star(
          x + Phaser.Math.Between(-130, 130),
          y + Phaser.Math.Between(-80, 80),
          5,
          Phaser.Math.Between(4, 7),
          Phaser.Math.Between(12, 20),
          Phaser.Math.RND.pick(colors),
          0.92,
        )
        .setDepth(8)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.tweens.add({
        targets: sparkle,
        y: sparkle.y - Phaser.Math.Between(40, 100),
        x: sparkle.x + Phaser.Math.Between(-28, 28),
        angle: Phaser.Math.Between(-120, 120),
        alpha: 0,
        scaleX: 0.25,
        scaleY: 0.25,
        duration: Phaser.Math.Between(520, 920),
        ease: 'Cubic.easeOut',
        onComplete: () => sparkle.destroy(),
      });
    }
  }

  private scheduleReynaBlink(rig: ReynaRig) {
    this.time.delayedCall(Phaser.Math.Between(2600, 5400), () => {
      if (!rig.root.active) {
        return;
      }

      const fullBlink = Math.random() < 0.26;

      rig.rightEyeClosed.setAlpha(1);
      rig.rightEye.setAlpha(0);
      rig.rightPupil.setAlpha(0);
      rig.rightPupilGlint.setAlpha(0);

      if (fullBlink) {
        rig.leftEyeClosed.setAlpha(1);
        rig.leftEye.setAlpha(0);
        rig.leftPupil.setAlpha(0);
        rig.leftPupilGlint.setAlpha(0);
      }

      this.tweens.add({
        targets: rig.rightBrow,
        y: rig.rightBrow.y + 6,
        duration: 80,
        yoyo: true,
        ease: 'Quad.easeOut',
      });

      this.time.delayedCall(fullBlink ? 95 : 165, () => {
        if (!rig.root.active) {
          return;
        }

        rig.leftEye.setAlpha(1);
        rig.leftPupil.setAlpha(1);
        rig.leftPupilGlint.setAlpha(1);
        rig.leftEyeClosed.setAlpha(0);
        rig.rightEye.setAlpha(1);
        rig.rightPupil.setAlpha(1);
        rig.rightPupilGlint.setAlpha(1);
        rig.rightEyeClosed.setAlpha(0);
        this.scheduleReynaBlink(rig);
      });
    });
  }

  private scheduleReynaLook(rig: ReynaRig) {
    this.time.delayedCall(Phaser.Math.Between(900, 2100), () => {
      if (!rig.root.active) {
        return;
      }

      const offsetX = Phaser.Math.Between(-2, 2);
      const offsetY = Phaser.Math.Between(-1, 1);
      const winkRightEye = Math.random() < 0.14;

      if (winkRightEye) {
        rig.rightEye.setAlpha(0);
        rig.rightPupil.setAlpha(0);
        rig.rightPupilGlint.setAlpha(0);
        rig.rightEyeClosed.setAlpha(1);
      }

      this.tweens.add({
        targets: rig.leftPupil,
        x: rig.baseLeftPupil.x + offsetX,
        y: rig.baseLeftPupil.y + offsetY,
        duration: 220,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: rig.leftPupilGlint,
        x: rig.baseLeftPupilGlint.x + offsetX,
        y: rig.baseLeftPupilGlint.y + offsetY,
        duration: 220,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: rig.rightPupil,
        x: rig.baseRightPupil.x + offsetX,
        y: rig.baseRightPupil.y + offsetY,
        duration: 220,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: rig.rightPupilGlint,
        x: rig.baseRightPupilGlint.x + offsetX,
        y: rig.baseRightPupilGlint.y + offsetY,
        duration: 220,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          if (winkRightEye && rig.root.active) {
            this.time.delayedCall(420, () => {
              if (!rig.root.active) {
                return;
              }

              rig.rightEye.setAlpha(1);
              rig.rightPupil.setAlpha(1);
              rig.rightPupilGlint.setAlpha(1);
              rig.rightEyeClosed.setAlpha(0);
            });
          }

          this.scheduleReynaLook(rig);
        },
      });
    });
  }

  private scheduleReynaEarTwitch(rig: ReynaRig) {
    this.time.delayedCall(Phaser.Math.Between(1200, 2600), () => {
      if (!rig.root.active) {
        return;
      }

      this.tweens.add({
        targets: Phaser.Math.RND.pick([rig.leftEar, rig.rightEar]),
        angle: '+=13',
        duration: 90,
        yoyo: true,
        repeat: 1,
        ease: 'Quad.easeOut',
        onComplete: () => this.scheduleReynaEarTwitch(rig),
      });
    });
  }

  private animateReynaReaction(rig: ReynaRig) {
    rig.leftEyeClosed.setAlpha(1);
    rig.rightEyeClosed.setAlpha(1);
    rig.leftEye.setAlpha(0);
    rig.rightEye.setAlpha(0);
    rig.leftPupil.setAlpha(0);
    rig.rightPupil.setAlpha(0);
    rig.leftPupilGlint.setAlpha(0);
    rig.rightPupilGlint.setAlpha(0);

    this.time.delayedCall(135, () => {
      if (!rig.root.active) {
        return;
      }

      rig.leftEye.setAlpha(1);
      rig.rightEye.setAlpha(1);
      rig.leftPupil.setAlpha(1);
      rig.rightPupil.setAlpha(1);
      rig.leftPupilGlint.setAlpha(1);
      rig.rightPupilGlint.setAlpha(1);
      rig.leftEyeClosed.setAlpha(0);
      rig.rightEyeClosed.setAlpha(0);
    });

    this.tweens.add({
      targets: rig.noseMouth,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 120,
      yoyo: true,
      ease: 'Back.easeOut',
    });
  }

  private createIntroOtto(
    x: number,
    y: number,
    options: {
      messages?: string[];
      bubbleX?: number;
      bubbleY?: number;
      depth?: number;
      initialScale?: number;
      targetScale?: number;
    } = {},
  ) {
    this.stopIntroOtto();
    this.ottoSpeechMessages = options.messages ?? OTTO_INTRO_MESSAGES;

    const root = this.add
      .container(x, y)
      .setDepth(options.depth ?? 2)
      .setScale(options.initialScale ?? 0.72)
      .setAlpha(0);
    const shadow = this.add.ellipse(0, 276, 390, 64, 0x21411f, 0.18);
    const leftPaw = this.add
      .image(-86, 34, OTAMA_ASSETS.ottoPaw1)
      .setOrigin(0.86, 0.88)
      .setScale(0.76)
      .setAngle(-7);
    const rightPaw = this.add
      .image(86, 32, OTAMA_ASSETS.ottoPaw2)
      .setOrigin(0.14, 0.88)
      .setScale(0.76)
      .setAngle(7);
    const body = this.add.image(0, 94, OTAMA_ASSETS.ottoBody).setOrigin(0.5).setScale(0.83, 0.61);
    const headGroup = this.add.container(0, -166);
    const leftEar = this.add.image(-104, -28, OTAMA_ASSETS.ottoEar1).setOrigin(0.78, 0.38).setAngle(-4);
    const rightEar = this.add.image(104, -28, OTAMA_ASSETS.ottoEar2).setOrigin(0.22, 0.38).setAngle(4);
    const head = this.add.image(0, 0, OTAMA_ASSETS.ottoHead).setOrigin(0.5);
    const leftBrow = this.add.image(-48, -78, OTAMA_ASSETS.ottoBrow1).setOrigin(0.5).setAngle(-8);
    const rightBrow = this.add.image(46, -78, OTAMA_ASSETS.ottoBrow2).setOrigin(0.5).setAngle(8);
    const leftEye = this.add.image(-34, -36, OTAMA_ASSETS.ottoEye1).setOrigin(0.5);
    const rightEye = this.add.image(33, -36, OTAMA_ASSETS.ottoEye2).setOrigin(0.5);
    const leftEyeClosed = this.add.image(-35, -36, OTAMA_ASSETS.ottoBrow1).setOrigin(0.5).setAngle(8).setScale(1.18).setAlpha(0);
    const rightEyeClosed = this.add
      .image(34, -36, OTAMA_ASSETS.ottoBrow2)
      .setOrigin(0.5)
      .setAngle(-8)
      .setScale(1.18)
      .setAlpha(0);
    const noseMouth = this.add.image(0, 58, OTAMA_ASSETS.ottoNoseMouth).setOrigin(0.5);
    const closedMouth = this.add.image(0, 58, OTAMA_ASSETS.ottoMouthClosed).setOrigin(0.5).setAlpha(0);
    const scarf = this.add
      .image(-88, -24, OTAMA_ASSETS.ottoScarf)
      .setOrigin(0.2, 0.48)
      .setScale(0.86, 0.78)
      .setAngle(-2);
    const hitZone = this.add.rectangle(0, 0, 820, 610, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    const bubble = this.createOttoSpeechBubble(options.bubbleX ?? 540, options.bubbleY ?? -220);

    hitZone.on(
      Phaser.Input.Events.POINTER_DOWN,
      (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        this.reactIntroOtto();
      },
    );

    headGroup.add([
      leftEar,
      rightEar,
      head,
      leftBrow,
      rightBrow,
      leftEye,
      rightEye,
      leftEyeClosed,
      rightEyeClosed,
      noseMouth,
      closedMouth,
    ]);
    root.add([shadow, body, leftPaw, rightPaw, headGroup, scarf, hitZone, bubble.container]);

    this.ottoSpeechIndex = 0;
    this.ottoIntroRig = {
      root,
      hitZone,
      body,
      headGroup,
      leftEar,
      rightEar,
      leftEye,
      rightEye,
      leftEyeClosed,
      rightEyeClosed,
      leftBrow,
      rightBrow,
      noseMouth,
      closedMouth,
      leftPaw,
      rightPaw,
      scarf,
      bubble: bubble.container,
      speechText: bubble.text,
      baseLeftEye: new Phaser.Math.Vector2(leftEye.x, leftEye.y),
      baseRightEye: new Phaser.Math.Vector2(rightEye.x, rightEye.y),
      baseNoseMouth: new Phaser.Math.Vector2(noseMouth.x, noseMouth.y),
    };

    this.tweens.add({
      targets: root,
      alpha: 1,
      scaleX: options.targetScale ?? 0.82,
      scaleY: options.targetScale ?? 0.82,
      y: y - 8,
      duration: 560,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: root,
      y: y - 22,
      duration: 1850,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 520,
    });
    this.tweens.add({
      targets: headGroup,
      angle: 3,
      x: 5,
      y: -172,
      duration: 1350,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: leftEar,
      angle: -8,
      duration: 1250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: rightEar,
      angle: 8,
      duration: 1250,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 90,
    });
    this.tweens.add({
      targets: leftPaw,
      angle: -13,
      x: -96,
      y: 26,
      duration: 720,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: rightPaw,
      angle: 13,
      x: 96,
      y: 24,
      duration: 760,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 90,
    });
    this.tweens.add({
      targets: scarf,
      angle: 2,
      scaleX: 0.88,
      scaleY: 0.94,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: [leftBrow, rightBrow],
      y: '-=7',
      duration: 1180,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: noseMouth,
      scaleX: 1.04,
      scaleY: 1.08,
      duration: 760,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.showOttoSpeech(0);
    this.scheduleOttoBlink();
    this.scheduleOttoScarfFlutter();
    this.scheduleOttoRandomBark();
    this.ottoSpeechTimer = this.time.addEvent({
      delay: 3300,
      loop: true,
      callback: () => this.showNextOttoSpeech(),
    });
    this.publishRuntimeState();
  }

  private createOttoSpeechBubble(x: number, y: number) {
    const container = this.add.container(x, y).setScale(0.85).setAlpha(0);
    const graphics = this.add.graphics();
    const glow = this.add.ellipse(0, 0, 390, 190, 0xffdf56, 0.18).setBlendMode(Phaser.BlendModes.ADD);
    const text = this.addText(0, -4, '', 31, '#24451f')
      .setOrigin(0.5)
      .setAlign('center')
      .setWordWrapWidth(310)
      .setLineSpacing(4);

    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillRoundedRect(-180, -86, 360, 172, 34);
    graphics.lineStyle(7, 0xffdf56, 1);
    graphics.strokeRoundedRect(-180, -86, 360, 172, 34);
    graphics.fillStyle(0xffffff, 0.96);
    graphics.fillTriangle(-134, 65, -80, 106, -72, 56);
    graphics.lineStyle(6, 0xffdf56, 1);
    graphics.lineBetween(-134, 65, -80, 106);
    graphics.lineBetween(-80, 106, -72, 56);
    container.add([glow, graphics, text]);

    this.tweens.add({
      targets: container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 420,
      ease: 'Back.easeOut',
      delay: 360,
    });
    this.tweens.add({
      targets: glow,
      alpha: 0.32,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 950,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return { container, text };
  }

  private scheduleOttoBlink() {
    this.ottoBlinkTimer?.remove(false);
    this.ottoBlinkTimer = this.time.delayedCall(Phaser.Math.Between(1550, 3100), () => {
      this.blinkIntroOtto();
      this.scheduleOttoBlink();
    });
  }

  private scheduleOttoScarfFlutter() {
    this.ottoScarfFlutterTimer?.remove(false);
    this.ottoScarfFlutterTimer = this.time.delayedCall(Phaser.Math.Between(420, 980), () => {
      const rig = this.ottoIntroRig;

      if (!rig || !rig.root.active || (this.screen !== 'intro' && this.screen !== 'register')) {
        return;
      }

      this.tweens.add({
        targets: rig.scarf,
        x: -88 + Phaser.Math.Between(-6, 8),
        y: -24 + Phaser.Math.Between(-5, 5),
        angle: Phaser.Math.Between(-8, 7),
        scaleX: Phaser.Math.FloatBetween(0.78, 0.94),
        scaleY: Phaser.Math.FloatBetween(0.72, 0.86),
        duration: Phaser.Math.Between(180, 360),
        yoyo: true,
        ease: 'Sine.easeInOut',
        onComplete: () => this.scheduleOttoScarfFlutter(),
      });
    });
  }

  private scheduleOttoRandomBark() {
    this.ottoRandomBarkTimer?.remove(false);
    this.ottoRandomBarkTimer = this.time.delayedCall(Phaser.Math.Between(4200, 8400), () => {
      const rig = this.ottoIntroRig;

      if (!rig || !rig.root.active || (this.screen !== 'intro' && this.screen !== 'register')) {
        return;
      }

      if (!this.ottoIsReacting && this.soundSystem.canPlayMedia()) {
        void this.soundSystem.playDogBark(0.78);
        this.animateIntroOttoMouth(680, 78);
        this.tweens.add({
          targets: rig.headGroup,
          angle: Phaser.Math.Between(-6, 6),
          duration: 110,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
      }

      this.scheduleOttoRandomBark();
    });
  }

  private animateIntroOttoMouth(durationMs: number, pulseMs = 92) {
    const rig = this.ottoIntroRig;

    if (!rig || !rig.root.active) {
      return;
    }

    this.ottoMouthTimer?.remove(false);
    let tick = 0;
    const totalTicks = Math.max(2, Math.ceil(durationMs / pulseMs));

    this.setOttoMouthClosed(false);
    this.ottoMouthTimer = this.time.addEvent({
      delay: pulseMs,
      repeat: totalTicks,
      callback: () => {
        if (!rig.root.active || (this.screen !== 'intro' && this.screen !== 'register')) {
          this.ottoMouthTimer?.remove(false);
          this.ottoMouthTimer = undefined;
          return;
        }

        tick += 1;

        if (tick >= totalTicks) {
          this.setOttoMouthClosed(false);
          this.ottoMouthTimer?.remove(false);
          this.ottoMouthTimer = undefined;
          return;
        }

        this.setOttoMouthClosed(tick % 2 === 1);
      },
    });
  }

  private blinkIntroOtto() {
    const rig = this.ottoIntroRig;

    if (!rig || !rig.root.active) {
      return;
    }

    this.setOttoEyesClosed(true);

    this.time.delayedCall(120, () => {
      if (!rig.root.active || this.ottoIsReacting) {
        return;
      }

      this.setOttoEyesClosed(false);
    });
  }

  private setOttoEyesClosed(closed: boolean) {
    const rig = this.ottoIntroRig;

    if (!rig) {
      return;
    }

    rig.leftEye.setAlpha(closed ? 0 : 1);
    rig.rightEye.setAlpha(closed ? 0 : 1);
    rig.leftEyeClosed.setAlpha(closed ? 1 : 0);
    rig.rightEyeClosed.setAlpha(closed ? 1 : 0);
  }

  private setOttoMouthClosed(closed: boolean) {
    const rig = this.ottoIntroRig;

    if (!rig) {
      return;
    }

    rig.noseMouth.setAlpha(closed ? 0 : 1);
    rig.closedMouth.setAlpha(closed ? 1 : 0);
  }

  private reactIntroOtto() {
    const rig = this.ottoIntroRig;

    if (!rig || !rig.root.active || (this.screen !== 'intro' && this.screen !== 'register')) {
      return;
    }

    this.ottoReactionTimer?.remove(false);
    this.ottoIsReacting = true;
    this.setOttoEyesClosed(true);
    this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
    void this.soundSystem.unlock().then(() => {
      if ((this.screen === 'intro' || this.screen === 'register') && rig.root.active) {
        this.soundSystem.startMenuMusic();
        const mouthDurationMs = this.soundSystem.playOttoTouchReaction();
        this.animateIntroOttoMouth(mouthDurationMs, 86);
      }
    });
    this.cameras.main.shake(90, 0.0018);

    this.ottoReactionTimer = this.time.delayedCall(2500, () => {
      this.ottoIsReacting = false;
      this.setOttoEyesClosed(false);
      this.setOttoMouthClosed(false);
      this.ottoReactionTimer = undefined;
    });
  }

  private updateIntroOttoPointerFollow() {
    const rig = this.ottoIntroRig;
    const pointer = this.input.activePointer;

    if (!rig || !rig.root.active || !pointer) {
      return;
    }

    if (pointer.x === 0 && pointer.y === 0) {
      rig.leftEye.x = Phaser.Math.Linear(rig.leftEye.x, rig.baseLeftEye.x, 0.2);
      rig.leftEye.y = Phaser.Math.Linear(rig.leftEye.y, rig.baseLeftEye.y, 0.2);
      rig.rightEye.x = Phaser.Math.Linear(rig.rightEye.x, rig.baseRightEye.x, 0.2);
      rig.rightEye.y = Phaser.Math.Linear(rig.rightEye.y, rig.baseRightEye.y, 0.2);
      return;
    }

    const point = this.getPointerWorldPoint(pointer);
    const localX = (point.x - rig.root.x) / rig.root.scaleX - rig.headGroup.x;
    const localY = (point.y - rig.root.y) / rig.root.scaleY - rig.headGroup.y;
    const leftOffsetX = Phaser.Math.Clamp((localX - rig.baseLeftEye.x) * 0.12, -8, 8);
    const rightOffsetX = Phaser.Math.Clamp((localX - rig.baseRightEye.x) * 0.12, -8, 8);
    const leftOffsetY = Phaser.Math.Clamp((localY - rig.baseLeftEye.y) * 0.08, -5, 5);
    const rightOffsetY = Phaser.Math.Clamp((localY - rig.baseRightEye.y) * 0.08, -5, 5);

    rig.leftEye.x = Phaser.Math.Linear(rig.leftEye.x, rig.baseLeftEye.x + leftOffsetX, 0.2);
    rig.leftEye.y = Phaser.Math.Linear(rig.leftEye.y, rig.baseLeftEye.y + leftOffsetY, 0.2);
    rig.rightEye.x = Phaser.Math.Linear(rig.rightEye.x, rig.baseRightEye.x + rightOffsetX, 0.2);
    rig.rightEye.y = Phaser.Math.Linear(rig.rightEye.y, rig.baseRightEye.y + rightOffsetY, 0.2);
    rig.leftBrow.angle = Phaser.Math.Linear(rig.leftBrow.angle, -8 + leftOffsetY * 0.7, 0.12);
    rig.rightBrow.angle = Phaser.Math.Linear(rig.rightBrow.angle, 8 - rightOffsetY * 0.7, 0.12);
  }

  private showNextOttoSpeech() {
    const nextIndex = (this.ottoSpeechIndex + 1) % this.ottoSpeechMessages.length;

    this.showOttoSpeech(nextIndex);
  }

  private showOttoSpeech(index: number) {
    const rig = this.ottoIntroRig;

    if (!rig || !rig.root.active) {
      return;
    }

    this.ottoSpeechIndex = index;
    const message = this.ottoSpeechMessages[index] ?? this.ottoSpeechMessages[0] ?? '';

    rig.speechText.setText(message);
    rig.speechText.setAlpha(0);
    rig.bubble.setScale(0.82);
    rig.bubble.setAngle(Phaser.Math.Between(-2, 2));

    this.tweens.add({
      targets: rig.bubble,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 260,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: rig.speechText,
      alpha: 1,
      duration: 180,
      ease: 'Sine.easeOut',
      delay: 70,
    });
    this.tweens.add({
      targets: rig.headGroup,
      angle: index % 2 === 0 ? -5 : 5,
      duration: 240,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: [rig.leftEye, rig.rightEye, rig.noseMouth],
      y: '+=4',
      duration: 150,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeOut',
      onComplete: () => {
        rig.leftEye.setPosition(rig.baseLeftEye.x, rig.baseLeftEye.y);
        rig.rightEye.setPosition(rig.baseRightEye.x, rig.baseRightEye.y);
        rig.noseMouth.setPosition(rig.baseNoseMouth.x, rig.baseNoseMouth.y);
      },
    });
    this.createOttoSparkles(rig.root.x + 250, rig.root.y - 330);
    runtimeWindow.__OTAMA_INTRO_SPEECH__ = message;
    this.publishRuntimeState();
  }

  private createOttoSparkles(x: number, y: number) {
    for (let index = 0; index < 14; index += 1) {
      const color = index % 3 === 0 ? 0xffdf56 : index % 3 === 1 ? 0xffffff : 0x7bd83e;
      const sparkle = this.add
        .circle(
          x + Phaser.Math.Between(-165, 165),
          y + Phaser.Math.Between(-75, 75),
          Phaser.Math.Between(4, 9),
          color,
          0.9,
        )
        .setDepth(5)
        .setBlendMode(Phaser.BlendModes.ADD);

      this.tweens.add({
        targets: sparkle,
        y: sparkle.y - Phaser.Math.Between(38, 95),
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: Phaser.Math.Between(520, 900),
        ease: 'Cubic.easeOut',
        onComplete: () => sparkle.destroy(),
      });
    }
  }

  private stopIntroOtto() {
    this.ottoBlinkTimer?.remove(false);
    this.ottoSpeechTimer?.remove(false);
    this.ottoReactionTimer?.remove(false);
    this.ottoScarfFlutterTimer?.remove(false);
    this.ottoMouthTimer?.remove(false);
    this.ottoRandomBarkTimer?.remove(false);
    this.ottoBlinkTimer = undefined;
    this.ottoSpeechTimer = undefined;
    this.ottoReactionTimer = undefined;
    this.ottoScarfFlutterTimer = undefined;
    this.ottoMouthTimer = undefined;
    this.ottoRandomBarkTimer = undefined;
    this.ottoIsReacting = false;

    if (this.ottoIntroRig?.root.active) {
      this.tweens.killTweensOf([
        this.ottoIntroRig.root,
        this.ottoIntroRig.body,
        this.ottoIntroRig.headGroup,
        this.ottoIntroRig.leftEar,
        this.ottoIntroRig.rightEar,
        this.ottoIntroRig.leftPaw,
        this.ottoIntroRig.rightPaw,
        this.ottoIntroRig.scarf,
        this.ottoIntroRig.leftBrow,
        this.ottoIntroRig.rightBrow,
        this.ottoIntroRig.noseMouth,
        this.ottoIntroRig.closedMouth,
        this.ottoIntroRig.bubble,
        this.ottoIntroRig.speechText,
      ]);
    }

    this.ottoIntroRig = undefined;
    runtimeWindow.__OTAMA_INTRO_OTTO_ACTIVE__ = false;
    runtimeWindow.__OTAMA_INTRO_SPEECH__ = undefined;
  }

  private saveLeadAndShowSelect(feedbackText?: Phaser.GameObjects.Text) {
    const name = this.readInputValue(this.nameInput);
    const contact = this.readInputValue(this.contactInput);

    if (!name || !contact) {
      feedbackText?.setText('Completa tus datos para comenzar el reto.');
      feedbackText?.setColor('#b0341e');
      this.tweens.add({
        targets: [this.nameInput, this.contactInput].filter(Boolean),
        scaleX: 1.04,
        scaleY: 1.04,
        duration: 110,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
      });
      this.cameras.main.shake(90, 0.0012);
      return;
    }

    this.currentParticipantName = name;
    this.currentParticipantContact = contact;

    const lead: LeadEntry = {
      name,
      contact,
      createdAt: new Date().toISOString(),
    };

    try {
      const leads = [lead, ...this.readLeads()].slice(0, 50);
      localStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify(leads));
    } catch {
      // The game can continue even if localStorage is unavailable.
    }

    feedbackText?.setText('Listo! Elige tu mascota y empieza.');
    feedbackText?.setColor('#24451f');
    this.soundSystem.playMenuStinger();
    this.time.delayedCall(220, () => {
      if (this.screen === 'register') {
        this.showSelect();
      }
    });
  }

  private saveEntryAndShowRanking() {
    if (this.currentRunSavedToRanking) {
      this.showRanking();
      return;
    }

    const name = this.currentParticipantName || this.readInputValue(this.nameInput) || 'Participante';
    const contact = this.currentParticipantContact || this.readInputValue(this.contactInput);
    const entry: RankingEntry = {
      name,
      contact,
      score: this.score,
      pet: this.selectedPet,
      createdAt: new Date().toISOString(),
    };
    const entries = [entry, ...this.readRanking()].sort((a, b) => b.score - a.score).slice(0, 10);

    try {
      localStorage.setItem(RANKING_STORAGE_KEY, JSON.stringify(entries));
      this.currentRunSavedToRanking = true;
    } catch {
      // Ranking persistence is best-effort for kiosk mode.
    }

    this.showRanking();
  }

  private returnToMainMenu() {
    this.soundSystem.stopAllLoops();
    this.clearGameplayTimers();
    this.showIntro();
  }

  private readRanking(): RankingEntry[] {
    const fallback: RankingEntry[] = [
      { name: 'Valeria', contact: '', score: 85, pet: 'dog', createdAt: 'seed' },
      { name: 'Mateo', contact: '', score: 70, pet: 'cat', createdAt: 'seed' },
      { name: 'Camila', contact: '', score: 66, pet: 'dog', createdAt: 'seed' },
    ];

    try {
      const stored = localStorage.getItem(RANKING_STORAGE_KEY);

      if (!stored) {
        return fallback;
      }

      const parsed = JSON.parse(stored) as RankingEntry[];
      return parsed.length > 0 ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  private readLeads(): LeadEntry[] {
    try {
      const stored = localStorage.getItem(LEAD_STORAGE_KEY);

      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored) as LeadEntry[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private addBackground(key: OtamaAssetKey) {
    return this.add.image(WIDTH / 2, HEIGHT / 2, key).setDisplaySize(WIDTH, HEIGHT).setDepth(-100);
  }

  private setScreen(screen: GameScreen) {
    this.screen = screen;
    this.publishRuntimeState();
  }

  private publishRuntimeState() {
    if (!shouldExposeRuntimeState) {
      return;
    }

    runtimeWindow.__OTAMA_GAME_SCREEN__ = this.screen;
    runtimeWindow.__OTAMA_INTRO_OTTO_ACTIVE__ = this.screen === 'intro' && Boolean(this.ottoIntroRig?.root.active);
    runtimeWindow.__OTAMA_INTRO_SPEECH__ = this.ottoIntroRig?.speechText.text;
    runtimeWindow.__OTAMA_GAME_SCORE__ = this.score;
    runtimeWindow.__OTAMA_GAME_HAIR_TOUCHES__ = this.hairTouches;
    runtimeWindow.__OTAMA_GAME_LIVES__ = this.getRemainingLives();
    runtimeWindow.__OTAMA_GAME_FRENZY_ACTIVE__ = this.isFrenzyActive();
    runtimeWindow.__OTAMA_GAME_RUN_DURATION_MS__ = this.runDurationMs;
    runtimeWindow.__OTAMA_GAME_FLEAS__ = this.fleas.map((flea) => {
      const bounds = flea.hitZone.getBounds();

      return {
        x: bounds.centerX,
        y: bounds.centerY,
        width: bounds.width,
        height: bounds.height,
      };
    });
    runtimeWindow.__OTAMA_GAME_TICK_BOSS__ = this.tickBoss
      ? {
          x: this.tickBoss.container.x,
          y: this.tickBoss.container.y,
          health: this.tickBoss.health,
        }
      : null;
    runtimeWindow.__OTAMA_GAME_DEBUG_HIT_TICK_BOSS__ = () => {
      if (!this.tickBoss) {
        return;
      }

      this.hitTickBoss(this.tickBoss.container.x, this.tickBoss.container.y);
      this.publishRuntimeState();
    };
    runtimeWindow.__OTAMA_GAME_MINI_TICKS__ = this.miniTicks.map((miniTick) => ({
      x: miniTick.sprite.x,
      y: miniTick.sprite.y,
    }));
    runtimeWindow.__OTAMA_GAME_BONUSES__ = this.bonusCollectibles.map((bonus) => ({
      x: bonus.container.x,
      y: bonus.container.y,
      kind: bonus.kind,
      value: bonus.value,
    }));
  }

  private getPointerWorldPoint(pointer: Phaser.Input.Pointer) {
    return this.cameras.main.getWorldPoint(pointer.x, pointer.y);
  }

  private addImage(key: OtamaAssetKey, x: number, y: number, maxWidth: number, maxHeight: number) {
    const image = this.add.image(x, y, key);
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height);

    return image.setScale(scale);
  }

  private addButton(
    key: OtamaAssetKey,
    x: number,
    y: number,
    maxWidth: number,
    maxHeight: number,
    onClick: () => void,
  ) {
    const ownerScreen = this.screen;
    const button = this.addImage(key, x, y, maxWidth, maxHeight);
    const baseScaleX = button.scaleX;
    const baseScaleY = button.scaleY;
    const minTouchTarget = gameSettings.kiosk.minTouchTarget;
    const hitTarget = this.add
      .rectangle(
        x,
        y,
        Math.max(button.displayWidth, maxWidth, minTouchTarget),
        Math.max(button.displayHeight, maxHeight, minTouchTarget),
        0xffffff,
        0.001,
      )
      .setInteractive({ useHandCursor: true })
      .setDepth(button.depth + 1);

    hitTarget.on(Phaser.Input.Events.POINTER_DOWN, () => {
      if (this.screen !== ownerScreen || !hitTarget.active) {
        return;
      }

      this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
      void this.soundSystem.unlock();
      this.soundSystem.playTap();
      onClick();
    });

    hitTarget.on(Phaser.Input.Events.POINTER_OVER, () => {
      if (this.screen === ownerScreen && button.active) {
        button.setScale(baseScaleX * 1.03, baseScaleY * 1.03);
      }
    });
    hitTarget.on(Phaser.Input.Events.POINTER_OUT, () => {
      if (button.active) {
        button.setScale(baseScaleX, baseScaleY);
      }
    });

    return button;
  }

  private addBackNavButton(onClick: () => void) {
    return this.addNavButton('Atrás', 132, 66, onClick, { tone: 'neutral', width: 196 });
  }

  private addMenuNavButton(label = 'Menú') {
    return this.addNavButton(label, WIDTH - 132, 66, () => this.returnToMainMenu(), { tone: 'primary', width: 196 });
  }

  private addRetryNavButton(onClick: () => void) {
    return this.addNavButton('Reintentar', 178, 66, onClick, { tone: 'warning', width: 264 });
  }

  private addNavButton(
    label: string,
    x: number,
    y: number,
    onClick: () => void,
    options: { tone?: NavButtonTone; width?: number; depth?: number } = {},
  ) {
    const ownerScreen = this.screen;
    const tone = options.tone ?? 'neutral';
    const width = options.width ?? 210;
    const height = 72;
    const depth = options.depth ?? 60;
    const minTouchTarget = gameSettings.kiosk.minTouchTarget;
    const palette = {
      neutral: { fill: 0xffffff, stroke: 0xffdf56, text: '#24451f', shadow: 0x24451f },
      primary: { fill: 0xff9800, stroke: 0xffffff, text: '#ffffff', shadow: 0x8a4b00 },
      warning: { fill: 0xffdf56, stroke: 0xffffff, text: '#24451f', shadow: 0x8a6d00 },
    }[tone];
    const container = this.add.container(x, y).setDepth(depth);
    const shadow = this.add.graphics();
    const bg = this.add.graphics();
    const text = this.addText(0, -2, label, 30, palette.text).setOrigin(0.5).setStroke('#ffffff', tone === 'primary' ? 0 : 4);
    const hitTarget = this.add
      .rectangle(x, y, Math.max(width, minTouchTarget), Math.max(height, minTouchTarget), 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(depth + 1);

    shadow.fillStyle(palette.shadow, 0.22);
    shadow.fillRoundedRect(-width / 2 + 4, -height / 2 + 6, width, height, 30);
    bg.fillStyle(palette.fill, 0.96);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, 30);
    bg.lineStyle(5, palette.stroke, 1);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 30);
    container.add([shadow, bg, text]);

    hitTarget.on(
      Phaser.Input.Events.POINTER_DOWN,
      (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();

        if (this.screen !== ownerScreen || !hitTarget.active) {
          return;
        }

        this.soundSystem.setEnabled(useGameStore.getState().audioEnabled);
        void this.soundSystem.unlock();
        this.soundSystem.playTap();
        onClick();
      },
    );
    hitTarget.on(Phaser.Input.Events.POINTER_OVER, () => {
      if (this.screen === ownerScreen && container.active) {
        container.setScale(1.04);
      }
    });
    hitTarget.on(Phaser.Input.Events.POINTER_OUT, () => {
      if (container.active) {
        container.setScale(1);
      }
    });

    return container;
  }

  private addChoiceLabel(x: number, y: number, label: string) {
    return this.addText(x, y, label, 42, '#254a21')
      .setOrigin(0.5)
      .setPadding(34, 14)
      .setBackgroundColor('#ffdf56');
  }

  private addText(x: number, y: number, text: string, size: number, color: string) {
    return this.add.text(x, y, text, {
      fontFamily: 'Chakra Petch',
      fontSize: `${size}px`,
      fontStyle: '700',
      color,
    });
  }

  private addInput(x: number, y: number, placeholder: string) {
    return this.add.dom(x, y).createFromHTML(`
      <input
        type="text"
        maxlength="42"
        placeholder="${placeholder}"
        style="
          width: 640px;
          height: 78px;
          border: 4px solid #24451f;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.96);
          box-shadow: 0 14px 28px rgba(36, 69, 31, 0.24);
          color: #24451f;
          font-family: Chakra Petch, sans-serif;
          font-size: 34px;
          font-weight: 700;
          padding: 0 28px;
          outline: none;
          text-align: center;
          pointer-events: auto;
        "
      />
    `);
  }

  private readInputValue(domElement?: Phaser.GameObjects.DOMElement) {
    const input = domElement?.node.querySelector('input') as HTMLInputElement | null;
    return input?.value.trim() ?? '';
  }

  private clearScreen() {
    this.destroyFormInputs();
    this.clearGameplayTimers();
    this.stopIntroOtto();
    this.destroyTickBoss();
    this.fleas.splice(0).forEach((flea) => this.destroyFlea(flea));
    this.miniTicks.splice(0).forEach((miniTick) => this.destroyMiniTick(miniTick));
    this.bonusCollectibles.splice(0).forEach((collectible) => this.destroyBonusCollectible(collectible));
    this.children.removeAll(true);
    this.lifeIcons.splice(0);
    this.livesText = undefined;
    this.frenzyOverlay = undefined;
    this.frenzyText = undefined;
  }

  private destroyFormInputs() {
    [this.nameInput, this.contactInput].forEach((domElement) => {
      if (!domElement) {
        return;
      }

      const node = domElement.node;
      domElement.destroy();

      if (node instanceof HTMLElement && node.isConnected) {
        node.remove();
      }
    });

    this.nameInput = undefined;
    this.contactInput = undefined;
  }

  private clearGameplayTimers() {
    this.spawnTimer?.remove(false);
    this.hairCueTimer?.remove(false);
    this.spawnTimer = undefined;
    this.hairCueTimer = undefined;
  }

  private handleShutdown() {
    this.input.off(Phaser.Input.Events.POINTER_DOWN, this.handlePointerDown);
    this.clearGameplayTimers();
    this.stopIntroOtto();
    this.soundSystem.destroy();
    useGameStore.getState().setSceneReady(false);
  }
}
